import DriverAssignedOrderCard from './DriverAssignedOrderCard';
import DriverExtraOrderCard from './DriverExtraOrderCard';
import DriverOrdersToolbar from './DriverOrdersToolbar';
import DriverBottomNavigation from './DriverBottomNavigation';
import { dailyText } from '../../components/dailyReport/dailyReportI18n';
import OrderSeatBadges from '../../components/OrderSeatBadges';
/**
 * 司机端订单列表页面
 * 手机端优先设计
 * 
 * 使用集中式API配置和DriverAPI服务
 */
import { startTransition, useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL, DriverAPI, TokenManager, isProductionEnvironment, isPreviewEnvironment, openDriverPdfDownloadUrl, resolveDriverDownloadUrl } from './config';
import ExtraScheduleModal from './ExtraScheduleModal';
import ExtraScheduleReceipt from '../../components/fleet/ExtraScheduleReceipt';
import DriverPoolSwipeControl from './DriverSwipeControl';
import DriverTripInstruction from './DriverTripInstruction';
import { getExtraScheduleText, extraDetailDefaults } from './extraScheduleFields';
import DriverAttendance from './DriverAttendance';
import { attendanceText } from '../../components/attendance/attendanceI18n';
import DriverTimeOffModal from './DriverTimeOffModal';
import DriverBrandHeader from './DriverBrandHeader';
import MonthSelect, { getMonthValue } from '../../components/admin/MonthSelect';
import { getTokyoDateKey, isDriverChatCompletedByOrderTime } from '../../utils/tokyoDate';
import { DRIVER_LANGUAGE_CHANGED_EVENT, formatDriverDate, getDriverHtmlLanguage, getDriverLanguage, getDriverText, getLocalizedDriverNotice } from './driverI18n';
import { getDriverFeatureText, getLocalizedDriverAnnouncement, getLocalizedDriverApiError } from './driverFeatureI18n';
import {
  DRIVER_CALENDAR_ADDED_EVENT,
  addDriverOrderToCalendar,
  getDriverCalendarAdded,
} from '../../services/driverApp/calendarService';
import {
  DRIVER_STATUS_QUEUE_CHANGED_EVENT,
  flushDriverStatusQueue,
  getDriverStatusQueueSummary,
  loadCachedDriverOrders,
  saveCachedDriverOrders,
} from '../../services/driverApp/offlineQueueService';
import {
  cacheFutureDriverOrderPack,
  clearDriverOfflineOrderPacksExcept,
  DRIVER_OFFLINE_ORDER_PACK_UPDATED_EVENT,
  getDriverOfflineOrderPackSummary,
  loadCachedDriverOfflineSignboardPdf,
  openCachedDriverOfflineSignboardPdf,
} from '../../services/driverApp/offlineOrderPackService';

const formatYen = (amount) => `¥${Math.round(amount || 0).toLocaleString()}`;

const formatDriverMonthLabel = (value, language) => {
  const [year, month] = String(value || '').split('-').map(Number);
  if (!year || !month) return value || '';
  return new Intl.DateTimeFormat(getDriverHtmlLanguage(language), {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
};

const getCalendarErrorMessage = (err, text) => {
  const message = err?.message || "";
  if (message === "ORDER_DATE_REQUIRED") return text.calendarDateMissing;
  if (message.includes("CALENDAR_PERMISSION_DENIED")) return text.calendarPermissionDenied;
  if (message.includes("not implemented") || message.includes("CALENDAR_PRESENTER_UNAVAILABLE")) {
    return text.calendarNativeUnavailable;
  }
  return text.submitFailed;
};

// 司机端车型显示统一使用系统标准英文名称，避免同一车型一会儿 alpha、一会儿阿尔法。
const driverVehicleTypeNames = {
  alpha: 'alphard',
  alphard: 'alphard',
  '阿尔法': 'alphard',
  hiace: 'hiace',
  premium_hiace: 'premium_hiace',
  coaster: 'coaster',
  bus: 'bus',
  hiace_14_seats: 'hiace_14_seats',
};

const DRIVER_SERVICE_LANGUAGE_OPTIONS = ['ja', 'en', 'es', 'ko', 'zh'];

const formatDriverVehicleType = (vehicleType) => {
  const raw = String(vehicleType || '').trim();
  if (!raw) return '-';
  return driverVehicleTypeNames[raw] || driverVehicleTypeNames[raw.toLowerCase()] || raw;
};

const isFleetDispatchableDriver = (driver) => (
  driver?.is_active !== false && !driver?.document_expiry_status?.has_expired
);

const isTruthyFlag = (value) => (
  value === true || value === 1 || String(value || '').toLowerCase() === 'true' || String(value || '') === '1'
);

const getFleetAssignedDriver = (order) => {
  const assigned = order?.assigned_driver || {};
  return {
    name: assigned.driver_name || assigned.name || order?.assigned_driver_name || '',
    phone: assigned.driver_phone || assigned.phone || order?.assigned_driver_phone || '',
    vehicle: [
      assigned.vehicle_brand || order?.vehicle_brand,
      assigned.vehicle_model || assigned.vehicle_type || order?.vehicle_type,
      assigned.plate_number || order?.plate_number,
    ].filter(Boolean).join(' · '),
  };
};

const FLEET_NON_DISPATCHABLE_STATUSES = new Set([
  'arrived_pickup',
  'passenger_onboard',
  'in_service',
  'completed',
  'cancelled',
  'canceled',
  'deleted',
]);

const isDriverOrderCompleted = (order) => (
  [order?.driver_order_status, order?.status]
    .some((status) => String(status || '').toLowerCase() === 'completed')
);

const isFleetOrderDispatchable = (order) => {
  if (!order?.fleet_owner_order) return false;
  const driverStatus = String(order?.driver_order_status || order?.status || '').toLowerCase();
  return !FLEET_NON_DISPATCHABLE_STATUSES.has(driverStatus);
};

function FleetMasterEmblem({ label, photoUrl, onClick, saving }) {
  const emblem = photoUrl ? <img src={photoUrl} alt={label} /> : (
    <>
      <svg viewBox="0 0 120 120" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="fleet-emblem-gold" x1="16" y1="10" x2="102" y2="112" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FFF4C5" />
            <stop offset="0.42" stopColor="#E5BA58" />
            <stop offset="1" stopColor="#8C641D" />
          </linearGradient>
        </defs>
        <circle cx="60" cy="60" r="55" fill="rgba(5, 5, 4, 0.92)" stroke="url(#fleet-emblem-gold)" strokeWidth="2" />
        <circle cx="60" cy="60" r="49" stroke="rgba(246, 217, 130, 0.4)" strokeWidth="1" />
        <path d="M46 29l7 6 7-11 7 11 7-6-2 15H48l-2-15Z" fill="url(#fleet-emblem-gold)" />
        <path d="M37 47.5v23.2c0 11.7 9.7 20.8 23 25.6 13.3-4.8 23-13.9 23-25.6V47.5L60 39 37 47.5Z" stroke="url(#fleet-emblem-gold)" strokeWidth="2.4" strokeLinejoin="round" />
        <text x="60" y="66" fill="#F6D982" fontSize="28" fontWeight="800" textAnchor="middle" fontFamily="Georgia, serif">BT</text>
        <path d="M27 50c-5 6-7 14-5 22M30 63c-2 5-2 10 0 15M93 50c5 6 7 14 5 22M90 63c2 5 2 10 0 15" stroke="rgba(246, 217, 130, 0.72)" strokeWidth="1.7" strokeLinecap="round" />
        <g stroke="#F6D982" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M43 82.5h10l-1.5-4.5h-7L43 82.5ZM45.5 82.5v2M50.5 82.5v2" />
          <path d="M55 82.5h10L63.5 78h-7L55 82.5ZM57.5 82.5v2M62.5 82.5v2" />
          <path d="M67 82.5h10L75.5 78h-7L67 82.5ZM69.5 82.5v2M74.5 82.5v2" />
        </g>
      </svg>
    </>
  );
  if (onClick) return <button type="button" className={`fleet-master-emblem fleet-master-emblem-button ${photoUrl ? 'fleet-master-emblem-has-photo' : ''}`} onClick={onClick} aria-label={label} disabled={saving} data-testid="fleet-master-emblem">{emblem}<span className="fleet-master-emblem-edit">{saving ? '...' : '＋'}</span></button>;
  return <div className="fleet-master-emblem" role="img" aria-label={label} data-testid="fleet-master-emblem">{emblem}</div>;
}

const formatDriverLocationLine = (area, detail, fallback) => {
  const clean = (value) => String(value || '').trim();
  const areaText = clean(area);
  const detailText = clean(detail);
  const fallbackText = clean(fallback);
  const parts = [];
  if (areaText) parts.push(areaText);
  if (detailText && detailText.toLowerCase() !== areaText.toLowerCase()) parts.push(detailText);
  if (!parts.length && fallbackText) parts.push(fallbackText);
  return parts.join(' / ') || '-';
};

const getDriverUnreadMessageCount = (order) => {
  const count = Number(order?.trip_chat_unread_count);
  if (Number.isFinite(count) && count >= 0) return count;
  return order?.trip_chat_unread ? 1 : 0;
};

const formatDriverConversationTimestamp = (value, language) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(getDriverHtmlLanguage(language), {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const getOrderChangeHighlights = (order, text) => {
  const labelMap = {
    time: text.orderChangeTime,
    pickup: text.orderChangePickup,
    dropoff: text.orderChangeDropoff,
    note: text.orderChangeNote,
    cancelled: text.orderChangeCancelled,
  };
  return (order?.driver_change_highlights || [])
    .map((highlight) => labelMap[highlight.field])
    .filter(Boolean);
};

const getDriverExtraServiceKey = (service) => {
  if (!service) return '';
  if (typeof service === 'string') return service;
  return service.service_type || service.type || service.id || service.value || '';
};

const buildDriverExtraServiceTags = (order, text) => {
  const labels = text.extraServiceTypes || {};
  const tags = [];
  const seen = new Set();
  const addTag = (key, fallback, suffix = '') => {
    const cleanKey = String(key || fallback || '').trim();
    if (!cleanKey) return;
    const label = labels[cleanKey] || fallback || cleanKey;
    const tag = `${label}${suffix}`;
    const dedupeKey = `${cleanKey}:${suffix}`;
    if (!seen.has(dedupeKey)) {
      seen.add(dedupeKey);
      tags.push(tag);
    }
  };

  (order?.extra_services || []).forEach((service) => {
    const key = getDriverExtraServiceKey(service);
    if (['child_seat', 'infant_seat'].includes(key)) return;
    if (['cancelled', 'canceled', 'rejected', 'deleted'].includes(service?.status)) return;
    const quantity = typeof service === 'object' ? Number(service.quantity || 0) : 0;
    if (key === 'extended_waiting_time') {
      const safeQuantity = Math.max(1, quantity || 1);
      const pickupArea = String(order?.pickup_area || '').toLowerCase();
      const baseMinutes = pickupArea.includes('airport') || pickupArea.includes('port') ? 90 : 30;
      addTag(key, labels[key], ` +${safeQuantity * 30} min / ${baseMinutes + safeQuantity * 30} min`);
      return;
    }
    const suffix = quantity > 1 ? ` x${quantity}` : '';
    addTag(key, typeof service === 'object' ? service.description : service, suffix);
  });

  if (order?.airport_pickup_sign) {
    addTag('airport_sign', labels.airport_sign);
  }

  return tags;
};

const ONBOARDING_MAX_DATA_URL_LENGTH = 2_700_000;

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const compressOnboardingImage = (dataUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onerror = reject;
  image.onload = () => {
    const maxEdge = 1600;
    const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext('2d');
    if (!context) {
      reject(new Error('ONBOARDING_FILE_READ_FAILED'));
      return;
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    let quality = 0.82;
    let compressed = canvas.toDataURL('image/jpeg', quality);
    while (compressed.length > ONBOARDING_MAX_DATA_URL_LENGTH && quality > 0.42) {
      quality -= 0.1;
      compressed = canvas.toDataURL('image/jpeg', quality);
    }
    if (compressed.length > ONBOARDING_MAX_DATA_URL_LENGTH) {
      reject(new Error('ONBOARDING_FILE_TOO_LARGE'));
      return;
    }
    resolve(compressed);
  };
  image.src = dataUrl;
});

const readOnboardingFile = async (file) => {
  if (!file) return null;
  const originalDataUrl = await fileToDataUrl(file);
  const isImage = String(file.type || '').toLowerCase().startsWith('image/');
  const dataUrl = isImage ? await compressOnboardingImage(originalDataUrl) : originalDataUrl;
  if (dataUrl.length > ONBOARDING_MAX_DATA_URL_LENGTH) {
    throw new Error('ONBOARDING_FILE_TOO_LARGE');
  }
  return {
    name: isImage ? `${String(file.name || 'document').replace(/\.[^.]+$/, '')}.jpg` : file.name,
    content_type: isImage ? 'image/jpeg' : file.type,
    data_url: dataUrl,
  };
};

const buildPlateNumberFromParts = (form = {}) => [
  form.plate_region,
  form.plate_class,
  form.plate_kana,
  form.plate_digits,
].map((value) => String(value || '').trim()).filter(Boolean).join(' ');

const splitPlateNumberToParts = (plateNumber = '') => {
  const parts = String(plateNumber || '').trim().split(/\s+/).filter(Boolean);
  return {
    plate_region: parts[0] || '',
    plate_class: parts[1] || '',
    plate_kana: parts[2] || '',
    plate_digits: parts.slice(3).join(' ') || '',
  };
};

const normalizeVehicleInfoForForm = (vehicleInfo = {}) => {
  const plateParts = splitPlateNumberToParts(vehicleInfo.plate_number);
  return {
    ...vehicleInfo,
    plate_region: vehicleInfo.plate_region || plateParts.plate_region,
    plate_class: vehicleInfo.plate_class || plateParts.plate_class,
    plate_kana: vehicleInfo.plate_kana || plateParts.plate_kana,
    plate_digits: vehicleInfo.plate_digits || plateParts.plate_digits,
    vehicle_model: vehicleInfo.vehicle_model || 'alphard',
  };
};

let squarePaymentsSdkPromise = null;

const loadSquarePaymentsSdk = (sdkUrl) => {
  if (window.Square?.payments) {
    return Promise.resolve(window.Square);
  }
  if (squarePaymentsSdkPromise) {
    return squarePaymentsSdkPromise;
  }
  squarePaymentsSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    script.onload = () => {
      if (window.Square?.payments) {
        resolve(window.Square);
      } else {
        reject(new Error('Square payments SDK was not loaded'));
      }
    };
    script.onerror = () => reject(new Error('Square payments SDK failed to load'));
    document.head.appendChild(script);
  });
  return squarePaymentsSdkPromise;
};

function OnboardingStepBadge({ status }) {
  const isApproved = status === 'approved';
  return (
    <span className={`driver-onboarding-step-dot ${isApproved ? 'is-done' : ''}`}>
      {isApproved ? '✓' : ''}
    </span>
  );
}

// 状态颜色
const statusColors = {
  pending: 'premium-chip-warning',
  confirmed: 'premium-chip-success',
  completed: 'bg-surface text-foreground',
  cancelled: 'premium-chip-destructive',
};

const extraStatusColors = {
  active: 'premium-chip',
  cancelled: 'premium-chip-destructive',
};


const TIME_OPTIONS = [
  '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30',
  '04:00', '04:30', '05:00', '05:30', '06:00', '06:30', '07:00', '07:30',
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
  '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30',
];

const DRIVER_EXTRA_SCHEDULES_CACHE_KEY = 'driver_extra_schedules_cache_v1';

const readDriverSessionJson = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeDriverSessionJson = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session cache is best effort.
  }
};

const buildOrdersSignature = (orders) => JSON.stringify((orders || []).map((order) => ({
  id: order.id,
  status: order.status,
  driver_order_status: order.driver_order_status,
  trip_chat_available: order.trip_chat_available,
  trip_chat_unread: order.trip_chat_unread,
  trip_chat_unread_count: order.trip_chat_unread_count,
  trip_chat_last_message_at: order.trip_chat_last_message_at,
  trip_chat_last_sender_role: order.trip_chat_last_sender_role,
  departure_date: order.departure_date,
  pickup_time: order.pickup_time,
  pickup_location_detail: order.pickup_location_detail,
  pickup_location: order.pickup_location,
  pickup_area: order.pickup_area,
  dropoff_location_detail: order.dropoff_location_detail,
  destination: order.destination,
  dropoff_area: order.dropoff_area,
  customer_name: order.customer_name,
  customer_code: order.customer_code,
  vehicle_type: order.vehicle_type,
  service_type: order.service_type,
  selected_hours: order.selected_hours,
  reported_end_time: order.reported_end_time,
  driver_dispatch_price_visible: order.driver_dispatch_price_visible,
  driver_dispatch_price_amount: order.driver_dispatch_price_amount,
  fleet_owner_order: order.fleet_owner_order,
  fleet_assignment_locked: order.fleet_assignment_locked,
  assigned_driver_id: order.assigned_driver_id,
  assigned_driver: order.assigned_driver,
})));

const buildExtraSchedulesSignature = (schedules) => JSON.stringify((schedules || []).map((schedule) => ({
  id: schedule.id,
  date: schedule.date,
  time: schedule.time,
  status: schedule.status,
  source_type: schedule.source_type,
  pickup_location: schedule.pickup_location,
  dropoff_location: schedule.dropoff_location,
  notes: schedule.notes,
  created_by_name: schedule.created_by_name,
  service_type: schedule.service_type,
  vehicle_type: schedule.vehicle_type,
  ...Object.fromEntries(Object.keys(extraDetailDefaults).map(key => [key, schedule[key]])),
  settlement_status: schedule.settlement_status,
  updated_at: schedule.updated_at,
})));

const driverOrdersViewCache = {
  driverProfile: null,
  orders: null,
  extraSchedules: null,
  filter: 'upcoming',
  monthlyStatsMonth: getMonthValue(),
  monthlyStats: null,
  activeDriverTab: 'orders',
  chatCategory: 'active',
  scrollY: 0,
};

const lockWindowScrollPosition = (targetY, durationMs = 900) => {
  const y = Math.max(0, Number(targetY || 0));
  const startedAt = Date.now();
  const tick = () => {
    window.scrollTo(0, y);
    if (Date.now() - startedAt < durationMs) {
      window.requestAnimationFrame(tick);
    }
  };
  tick();
};

export default function DriverOrders({ driver, onLogout }) {
  const initialOrdersRef = useRef(null);
  const initialExtraSchedulesRef = useRef(null);
  if (initialOrdersRef.current === null) {
    initialOrdersRef.current = driverOrdersViewCache.orders || loadCachedDriverOrders()?.orders || [];
  }
  if (initialExtraSchedulesRef.current === null) {
    initialExtraSchedulesRef.current = driverOrdersViewCache.extraSchedules || readDriverSessionJson(DRIVER_EXTRA_SCHEDULES_CACHE_KEY, []);
  }
  const [driverProfile, setDriverProfile] = useState(() => driverOrdersViewCache.driverProfile || driver || TokenManager.getDriverInfo() || null);
  const [orders, setOrders] = useState(() => initialOrdersRef.current);
  const [extraSchedules, setExtraSchedules] = useState(() => initialExtraSchedulesRef.current);
  const [loading, setLoading] = useState(() => initialOrdersRef.current.length === 0 && initialExtraSchedulesRef.current.length === 0);
  const [error, setError] = useState('');
  const [usingCachedData, setUsingCachedData] = useState(false);
  const [retryNotice, setRetryNotice] = useState('');
  const [offlineOrderPackSummary, setOfflineOrderPackSummary] = useState({ orderCount: 0, pdfCount: 0, pendingPdfCount: 0, updatedAt: '' });
  const [filter, setFilter] = useState(driverOrdersViewCache.filter); // upcoming, all, completed
  const [monthlyStatsMonth, setMonthlyStatsMonth] = useState(driverOrdersViewCache.monthlyStatsMonth);
  const [monthlyStats, setMonthlyStats] = useState(driverOrdersViewCache.monthlyStats);
  const [monthlyStatsLoading, setMonthlyStatsLoading] = useState(false);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [showScoreDetails, setShowScoreDetails] = useState(false);
  const [scoreEvents, setScoreEvents] = useState([]);
  const [scoreEventsLoading, setScoreEventsLoading] = useState(false);
  const [activeDriverTab, setActiveDriverTab] = useState(driverOrdersViewCache.activeDriverTab);
  const [chatCategory, setChatCategory] = useState(driverOrdersViewCache.chatCategory);
  const [chatClock, setChatClock] = useState(() => Date.now());
  const [orderPool, setOrderPool] = useState([]);
  const [orderPoolMeta, setOrderPoolMeta] = useState({ driver_score: 500, can_accept_order_pool: true });
  const [orderPoolLoading, setOrderPoolLoading] = useState(false);
  const [orderPoolActionId, setOrderPoolActionId] = useState(null);
  const [onboarding, setOnboarding] = useState(driverProfile?.onboarding || null);
  const [profileSection, setProfileSection] = useState('');
  const [trainingLessons, setTrainingLessons] = useState(() => getDriverFeatureText(getDriverLanguage()).trainingFallback);
  const trainingLessonsRef = useRef(trainingLessons);
  trainingLessonsRef.current = trainingLessons;
  const [trainingQuestions, setTrainingQuestions] = useState([]);
  const [inspectionOptions, setInspectionOptions] = useState(null);
  const [onboardingSaving, setOnboardingSaving] = useState('');
  const [personalInfoForm, setPersonalInfoForm] = useState({
    full_name: driverProfile?.name || driver?.name || '',
    date_of_birth: '',
    address: '',
    service_languages: [],
    is_japanese_national: false,
    residence_card_number: '',
    residence_card_expiry: '',
    residence_card_long_term_valid: false,
    driver_license_number: '',
    driver_license_expiry: '',
  });
  const [personalFiles, setPersonalFiles] = useState({});
  const profilePhotoInputRef = useRef(null);
  const profilePhotoCameraInputRef = useRef(null);
  const [showProfilePhotoMenu, setShowProfilePhotoMenu] = useState(false);
  const [vehicleInfoForm, setVehicleInfoForm] = useState({
    plate_number: '',
    plate_region: '',
    plate_class: '',
    plate_kana: '',
    plate_digits: '',
    vehicle_model: 'alphard',
    vehicle_color: 'black',
    vehicle_year: new Date().getFullYear(),
    optional_insurance_company: '',
    optional_insurance_policy_number: '',
    optional_insurance_expiry: '',
    compulsory_insurance_expiry: '',
  });
  const [vehicleFiles, setVehicleFiles] = useState({});
  const [trainingAnswers, setTrainingAnswers] = useState({});
  const [trainingLessonIndex, setTrainingLessonIndex] = useState(0);
  const [trainingConfirmedLessons, setTrainingConfirmedLessons] = useState({});
  const [trainingWrongQuestions, setTrainingWrongQuestions] = useState([]);
  const [inspectionForm, setInspectionForm] = useState({
    inspection_site_id: 'big_taxi_default',
    appointment_date: '',
    appointment_time: '',
    confirmation_items: [],
    note: '',
  });
  const [inspectionStep, setInspectionStep] = useState('site');
  const [squarePaymentConfig, setSquarePaymentConfig] = useState(null);
  const [squarePaymentError, setSquarePaymentError] = useState('');
  const [squareCardReady, setSquareCardReady] = useState(false);
  const [squarePaymentProcessing, setSquarePaymentProcessing] = useState(false);
  const [documentManagementForm, setDocumentManagementForm] = useState({});
  const [documentManagementFiles, setDocumentManagementFiles] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  
  // 汇报包车时间相关状态
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reportEndTime, setReportEndTime] = useState('');
  const [reporting, setReporting] = useState(false);
  
  // 追加订单弹窗状态
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [cancellingExtra, setCancellingExtra] = useState(null);
  const [showTimeOffModal, setShowTimeOffModal] = useState(false);
  const [generatingSignboardOrderId, setGeneratingSignboardOrderId] = useState(null);
  const [fleetDispatchOrder, setFleetDispatchOrder] = useState(null);
  const [fleetDispatchDrivers, setFleetDispatchDrivers] = useState([]);
  const [fleetDispatchVehicles, setFleetDispatchVehicles] = useState([]);
  const [fleetIdentityStats, setFleetIdentityStats] = useState({ drivers: null, vehicles: null });
  const [fleetDispatchSelectedDriver, setFleetDispatchSelectedDriver] = useState(null);
  const [fleetDispatchStep, setFleetDispatchStep] = useState('driver');
  const [fleetDispatchVehicleDraft, setFleetDispatchVehicleDraft] = useState({ vehicle_type: 'alphard', plate_number: '' });
  const [fleetDispatchLoading, setFleetDispatchLoading] = useState(false);
  const [fleetDispatchSaving, setFleetDispatchSaving] = useState('');
  const [, setCalendarStateVersion] = useState(0);
  const [showNoticesModal, setShowNoticesModal] = useState(false);
  const [driverNotices, setDriverNotices] = useState([]);
  const [driverNoticesLoading, setDriverNoticesLoading] = useState(false);
  const [driverLanguage, setDriverLanguage] = useState(() => getDriverLanguage());
  const extraCopy = getExtraScheduleText(driverLanguage);
  const [settlingExtra, setSettlingExtra] = useState(null);
  const [extraSettlementError, setExtraSettlementError] = useState(null);
  const settlingExtraRef = useRef(false);
  const changeExtraSettlement = async (schedule, status) => {
    if (settlingExtraRef.current) return;
    settlingExtraRef.current = true;
    setSettlingExtra(schedule.id); setExtraSettlementError(null);
    try {
      const updated = await DriverAPI.updateExtraSettlement(schedule.id, status);
      if (!updated) throw new Error('Missing updated booking');
      setExtraSchedules(current => current.map(item => item.id === updated.id ? updated : item));
      await fetchExtraSchedules();
    } catch {
      setExtraSettlementError({ id: schedule.id, message: extraCopy.failed });
    } finally {
      settlingExtraRef.current = false; setSettlingExtra(null);
    }
  };
  
  // Safari 兼容 - 使用 ref 操作原生 DOM
  const selectRef = useRef(null);
  const squareCardRef = useRef(null);
  const ordersSignatureRef = useRef(buildOrdersSignature(initialOrdersRef.current));
  const extraSchedulesSignatureRef = useRef(buildExtraSchedulesSignature(initialExtraSchedulesRef.current));
  const returningFromSwipeBackRef = useRef(Boolean(location.state?.driverSwipeBackReturn));
  const stabilizeUntilRef = useRef(returningFromSwipeBackRef.current ? Date.now() + 3200 : 0);
  const offlineOrderPackSyncingRef = useRef(false);
  const canViewRevenueStats = driverProfile?.can_view_revenue_stats === true;
  const attendanceEnabled = driverProfile?.attendance_enabled === true;
  const attendanceTitle = attendanceText(driverLanguage).title;
  const canRequestLeave = driverProfile?.can_request_leave === true;
  const canAcceptOrderPool = orderPoolMeta.can_accept_order_pool !== false;
  const text = getDriverText(driverLanguage);
  const featureText = getDriverFeatureText(driverLanguage);
  const offlineOrderPackDriverId = String(driverProfile?.id || driver?.id || TokenManager.getDriverInfo()?.id || '').trim();
  const consoleSettings = driverProfile?.console_settings || {};
  const orderPoolSettings = consoleSettings.order_pool || {};
  const driverDisplaySettings = consoleSettings.driver_display || {};
  const isFleetMasterAccount = isTruthyFlag(driverProfile?.is_fleet_master);
  const orderPoolOpacity = Math.max(0.05, Math.min(1, Number(orderPoolSettings.opacity ?? 95) / 100));
  const orderPoolDensity = orderPoolSettings.density || 'comfortable';
  const orderPoolGap = orderPoolDensity === 'compact' ? '0.75rem' : orderPoolDensity === 'spacious' ? '1.25rem' : '1rem';
  const orderPoolCardPadding = orderPoolDensity === 'compact' ? '0.85rem' : orderPoolDensity === 'spacious' ? '1.25rem' : '1.1rem';
  const orderPoolRefreshIntervalMs = Math.max(5000, Number(orderPoolSettings.refresh_interval || 30) * 1000);
  const driverAnnouncement = driverDisplaySettings.show_announcement === false
    ? ''
    : getLocalizedDriverAnnouncement(driverDisplaySettings.announcement, driverLanguage);
  const profileDriverName = driverProfile?.name || driver?.name || text.driver;
  const profileDriverPhone = driverProfile?.phone || driver?.phone || '';
  const profilePhotoUrl = personalFiles.profile_photo?.data_url || onboarding?.personal_info?.profile_photo?.data_url || '';
  const fleetDispatchableOrderCount = useMemo(() => orders.filter((order) => order.departure_date >= getTokyoDateKey() && order.status !== 'cancelled').length, [orders]);
  const driverScore = orderPoolMeta.driver_score ?? driverProfile?.driver_score ?? 500;
  const profileSettlementTotals = monthlyStats?.totals || {};
  const onboardingSteps = onboarding?.steps || {};
  const currentTrainingLesson = trainingLessons[trainingLessonIndex] || trainingLessons[0] || {};
  const trainingConfirmedCount = trainingLessons.filter((lesson) => trainingConfirmedLessons[lesson.id]).length;
  const trainingLessonsCompleted = trainingLessons.length > 0 && trainingConfirmedCount >= trainingLessons.length;
  const trainingProgressPercent = trainingLessons.length ? Math.round((trainingConfirmedCount / trainingLessons.length) * 100) : 0;
  const vehicleInspection = onboarding?.vehicle_inspection || {};
  const inspectionPaymentStatus = String(vehicleInspection.payment_status || '').toLowerCase();
  const getInspectionPaymentStatusLabel = (status) => {
    const normalized = String(status || 'pending').toLowerCase();
    return text.inspectionPaymentStatusLabels?.[normalized] || text.pending;
  };
  const inspectionAppointmentReady = Boolean(vehicleInspection.verification_code);
  const inspectionPaymentDone = ['paid', 'completed'].includes(inspectionPaymentStatus);
  const inspectionAppointmentFormReady = Boolean(inspectionForm.appointment_date && inspectionForm.appointment_time);
  const inspectionSites = inspectionOptions?.sites || [];
  const selectedInspectionSite = inspectionSites.find((site) => site.id === inspectionForm.inspection_site_id)
    || inspectionOptions?.site
    || null;
  const inspectionConfirmationReady = (inspectionOptions?.confirmation_items || []).every((item) => (
    (inspectionForm.confirmation_items || []).includes(item.id)
  ));
  const inspectionSiteConfirmed = inspectionStep !== 'site' || inspectionAppointmentReady || Boolean(
    vehicleInspection.inspection_site_id && (vehicleInspection.appointment_date || vehicleInspection.verification_code)
  );
  const inspectionStepStatus = {
    site: inspectionSiteConfirmed ? 'approved' : 'pending',
    appointment: inspectionAppointmentReady ? 'approved' : inspectionStep === 'appointment' ? 'submitted' : 'pending',
    confirmation: inspectionAppointmentReady ? 'approved' : inspectionStep === 'confirmation' ? 'submitted' : 'pending',
    payment: inspectionPaymentDone ? 'approved' : inspectionAppointmentReady ? 'submitted' : 'pending',
    qr: inspectionPaymentDone ? 'approved' : 'pending',
  };
  const documentExpiryStatus = driverProfile?.document_expiry_status || {};
  const expiredDocumentCount = documentExpiryStatus.expired?.length || 0;
  const upcomingDocumentCount = documentExpiryStatus.upcoming?.length || 0;
  const getExpiredDocumentLabel = (key) => text.documentExpiryNames?.[key] || key || text.documentManagement;
  const firstExpiredDocument = documentExpiryStatus.expired?.[0];
  const onboardingReviewIncomplete = driverProfile
    ? driverProfile.onboarding_final_approved !== true
    : orderPoolMeta.can_accept_order_pool === false && !firstExpiredDocument;
  const orderPoolAccessMessage = firstExpiredDocument
    ? text.orderPoolExpiredDocumentRequired(getExpiredDocumentLabel(firstExpiredDocument.key))
    : onboardingReviewIncomplete
      ? text.orderPoolOnboardingRequired
      : !canAcceptOrderPool
        ? text.orderPoolTemporarilyUnavailable
        : '';
  const profileOrderStatusMessage = canAcceptOrderPool
    ? text.canAcceptOrders
    : orderPoolAccessMessage || text.orderPoolOnboardingRequired;
  const profileOrderStatusTone = canAcceptOrderPool ? '' : firstExpiredDocument ? 'driver-profile-status-chip-danger' : 'driver-profile-status-chip-pending';
  const statusLabel = (status) => text.status?.[String(status || '').toLowerCase()] || text.unknownStatus;
  const driverStatusLabel = (status) => text.driverStatus?.[String(status || '').toLowerCase()] || text.unknownStatus;
  const serviceTypeLabel = (serviceType) => text.serviceType?.[String(serviceType || '').toLowerCase()] || text.unknownStatus;
  const extraStatusLabel = (status) => (
    status === 'cancelled' ? text.cancelled : status === 'active' ? text.active : text.unknownStatus
  );
  const getOnboardingReviewProgress = (step) => {
    const status = onboardingSteps[step]?.status || 'pending';
    const percent = status === 'approved' ? 100 : status === 'submitted' ? 50 : 0;
    return {
      percent,
      status,
      title: text.onboardingSteps?.[step] || text.unknownStatus,
      label: text.onboardingStatus?.[status] || text.unknownStatus,
    };
  };
  const renderOnboardingDetailProgress = (step) => {
    const progress = getOnboardingReviewProgress(step);
    return (
      <div className="driver-onboarding-detail-progress" data-testid={`driver-onboarding-detail-progress-${step}`}>
        <div className="driver-onboarding-detail-progress-header">
          <span>{progress.title}</span>
          <strong>{progress.percent}%</strong>
        </div>
        <div
          className="driver-onboarding-detail-progress-bar"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={progress.percent}
        >
          <span style={{ width: `${progress.percent}%` }} />
        </div>
        <small>{progress.label}</small>
      </div>
    );
  };

  useEffect(() => {
    driverOrdersViewCache.driverProfile = driverProfile;
  }, [driverProfile]);

  useEffect(() => {
    const syncDriverLanguage = (event) => {
      setDriverLanguage(event?.detail?.language || getDriverLanguage());
    };
    window.addEventListener(DRIVER_LANGUAGE_CHANGED_EVENT, syncDriverLanguage);
    window.addEventListener('storage', syncDriverLanguage);
    return () => {
      window.removeEventListener(DRIVER_LANGUAGE_CHANGED_EVENT, syncDriverLanguage);
      window.removeEventListener('storage', syncDriverLanguage);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = getDriverHtmlLanguage(driverLanguage);
  }, [driverLanguage]);

  useEffect(() => {
    const refreshCalendarState = () => setCalendarStateVersion((version) => version + 1);
    window.addEventListener(DRIVER_CALENDAR_ADDED_EVENT, refreshCalendarState);
    return () => window.removeEventListener(DRIVER_CALENDAR_ADDED_EVENT, refreshCalendarState);
  }, []);

  useEffect(() => {
    driverOrdersViewCache.orders = orders;
  }, [orders]);

  useEffect(() => {
    driverOrdersViewCache.extraSchedules = extraSchedules;
  }, [extraSchedules]);

  useEffect(() => {
    driverOrdersViewCache.filter = filter;
  }, [filter]);

  useEffect(() => {
    driverOrdersViewCache.activeDriverTab = activeDriverTab;
  }, [activeDriverTab]);

  useEffect(() => {
    driverOrdersViewCache.chatCategory = chatCategory;
  }, [chatCategory]);

  useEffect(() => {
    if (activeDriverTab !== 'conversations') return undefined;
    setChatClock(Date.now());
    const interval = window.setInterval(() => setChatClock(Date.now()), 60000);
    return () => window.clearInterval(interval);
  }, [activeDriverTab]);

  useEffect(() => {
    driverOrdersViewCache.monthlyStatsMonth = monthlyStatsMonth;
  }, [monthlyStatsMonth]);

  useEffect(() => {
    driverOrdersViewCache.monthlyStats = monthlyStats;
  }, [monthlyStats]);

  useEffect(() => {
    if (inspectionPaymentDone) {
      setInspectionStep('qr');
    } else if (inspectionAppointmentReady) {
      setInspectionStep('payment');
    }
  }, [inspectionAppointmentReady, inspectionPaymentDone]);

  useEffect(() => {
    let cancelled = false;
    const initializeSquareCard = async () => {
      if (inspectionStep !== 'payment' || inspectionPaymentDone || squareCardRef.current) {
        return;
      }
      if (!squarePaymentConfig?.enabled) {
        setSquareCardReady(false);
        return;
      }
      try {
        setSquarePaymentError('');
        const Square = await loadSquarePaymentsSdk(squarePaymentConfig.sdk_url);
        if (cancelled) return;
        const payments = window.Square.payments(
          squarePaymentConfig.application_id,
          squarePaymentConfig.location_id
        );
        const card = await payments.card();
        await card.attach('#driver-square-card-container');
        if (cancelled) return;
        squareCardRef.current = card;
        setSquareCardReady(true);
      } catch (err) {
        if (cancelled) return;
        console.error('[DriverOrders] Square card setup error:', err);
        setSquareCardReady(false);
        setSquarePaymentError(text.paymentSetupRequired);
      }
    };
    initializeSquareCard();
    return () => {
      cancelled = true;
    };
  }, [inspectionPaymentDone, inspectionStep, squarePaymentConfig, text.paymentSetupRequired]);

  const navigateFromOrders = useCallback((path, options) => {
    const scrollY = Math.max(0, window.scrollY || 0);
    driverOrdersViewCache.scrollY = scrollY;
    sessionStorage.setItem('driverOrdersScrollY', String(scrollY));
    navigate(path, options);
  }, [navigate]);

  const preserveScrollIfReturning = useCallback(() => {
    if (Date.now() > stabilizeUntilRef.current) return;
    const currentY = window.scrollY;
    lockWindowScrollPosition(currentY, 520);
  }, []);

  const setOrdersIfChanged = useCallback((nextOrders) => {
    const signature = buildOrdersSignature(nextOrders);
    if (signature === ordersSignatureRef.current) return;
    ordersSignatureRef.current = signature;
    startTransition(() => {
      setOrders(nextOrders || []);
    });
    preserveScrollIfReturning();
  }, [preserveScrollIfReturning]);

  const setExtraSchedulesIfChanged = useCallback((nextSchedules) => {
    const signature = buildExtraSchedulesSignature(nextSchedules);
    if (signature === extraSchedulesSignatureRef.current) return;
    extraSchedulesSignatureRef.current = signature;
    startTransition(() => {
      setExtraSchedules(nextSchedules || []);
    });
    writeDriverSessionJson(DRIVER_EXTRA_SCHEDULES_CACHE_KEY, nextSchedules || []);
    preserveScrollIfReturning();
  }, [preserveScrollIfReturning]);

  useLayoutEffect(() => {
    if (!returningFromSwipeBackRef.current) return undefined;
    const savedY = Number(sessionStorage.getItem('driverOrdersScrollY') || driverOrdersViewCache.scrollY || '0');
    lockWindowScrollPosition(savedY, 1600);
    const timer = window.setTimeout(() => {
      returningFromSwipeBackRef.current = false;
    }, 3300);
    return () => window.clearTimeout(timer);
  }, []);

  const fetchDriverProfile = useCallback(async () => {
    try {
      const data = await DriverAPI.getMe();
      setDriverProfile((current) => {
        if (JSON.stringify(current || {}) === JSON.stringify(data || {})) return current;
        if (Date.now() <= stabilizeUntilRef.current && current) {
          window.setTimeout(() => {
            setDriverProfile(data);
            TokenManager.setDriverInfo(data);
          }, Math.max(120, stabilizeUntilRef.current - Date.now()));
          return current;
        }
        preserveScrollIfReturning();
        return data;
      });
      TokenManager.setDriverInfo(data);
      return data;
    } catch (err) {
      console.error('[DriverOrders] fetchDriverProfile error:', err);
      setDriverProfile(driver || null);
      return driver || null;
    }
  }, [driver, preserveScrollIfReturning]);

  const refreshOfflineOrderPack = useCallback(async (nextOrders) => {
    if (!offlineOrderPackDriverId || offlineOrderPackSyncingRef.current) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setOfflineOrderPackSummary(await getDriverOfflineOrderPackSummary(offlineOrderPackDriverId));
      return;
    }

    offlineOrderPackSyncingRef.current = true;
    try {
      const summary = await cacheFutureDriverOrderPack({
        driverId: offlineOrderPackDriverId,
        orders: nextOrders,
        fetchOrderDetail: DriverAPI.getOrderDetail,
        fetchSignboardPdf: DriverAPI.downloadSignboardPdf,
      });
      setOfflineOrderPackSummary(summary);
    } finally {
      offlineOrderPackSyncingRef.current = false;
    }
  }, [offlineOrderPackDriverId]);

  useEffect(() => {
    if (!offlineOrderPackDriverId) return undefined;
    let cancelled = false;
    const loadOfflineOrderPackSummary = async () => {
      await clearDriverOfflineOrderPacksExcept(offlineOrderPackDriverId);
      const summary = await getDriverOfflineOrderPackSummary(offlineOrderPackDriverId);
      if (!cancelled) setOfflineOrderPackSummary(summary);
    };
    const handleOfflineOrderPackUpdated = (event) => {
      if (!cancelled) setOfflineOrderPackSummary(event.detail || { orderCount: 0, pdfCount: 0, pendingPdfCount: 0, updatedAt: '' });
    };

    loadOfflineOrderPackSummary();
    window.addEventListener(DRIVER_OFFLINE_ORDER_PACK_UPDATED_EVENT, handleOfflineOrderPackUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener(DRIVER_OFFLINE_ORDER_PACK_UPDATED_EVENT, handleOfflineOrderPackUpdated);
    };
  }, [offlineOrderPackDriverId]);

  useEffect(() => {
    let cancelled = false;

    const loadDriverProfile = async () => {
      const data = await fetchDriverProfile();
      if (cancelled) {
        return;
      }
      setDriverProfile(data);
    };

    loadDriverProfile();
    return () => {
      cancelled = true;
    };
  }, [fetchDriverProfile]);

  useEffect(() => {
    if (!isFleetMasterAccount || activeDriverTab !== 'profile') return undefined;

    let cancelled = false;
    Promise.all([DriverAPI.getFleetDrivers(), DriverAPI.getFleetVehicles()])
      .then(([drivers, vehicles]) => {
        if (!cancelled) {
          setFleetIdentityStats({
            drivers: Array.isArray(drivers) ? drivers.length : 0,
            vehicles: Array.isArray(vehicles) ? vehicles.length : 0,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setFleetIdentityStats({ drivers: 0, vehicles: 0 });
      });

    return () => {
      cancelled = true;
    };
  }, [activeDriverTab, isFleetMasterAccount]);

  const fetchOrders = useCallback(async () => {
    console.log('[DriverOrders] ============================================');
    console.log('[DriverOrders] fetchOrders called');
    console.log('[DriverOrders] API URL:', API_BASE_URL);
    console.log('[DriverOrders] ============================================');
    console.log(`[DriverOrders] Environment: ${isProductionEnvironment() ? 'PRODUCTION' : isPreviewEnvironment() ? 'PREVIEW' : 'LOCAL'}`);
    
    try {
      // 检查token是否存在
      if (!TokenManager.isAuthenticated()) {
        console.log('[DriverOrders] No token found, redirecting to login');
        setError(text.loginExpired);
        setTimeout(() => onLogout(), 1500);
        return;
      }
      
      console.log('[DriverOrders] Fetching orders via DriverAPI...');
      
      // 使用集中式DriverAPI（自动处理token和401）
      const data = await DriverAPI.getOrders();
      
      console.log('[DriverOrders] Orders count:', data?.length || 0);
      setOrdersIfChanged(data);
      saveCachedDriverOrders(data);
      refreshOfflineOrderPack(data);
      setUsingCachedData(false);
      setError(''); // 清除之前的错误
      
    } catch (err) {
      // 检查是否是安全检查失败
      if (err.message && err.message.includes('Safety Check Failed')) {
        console.error('[DriverOrders] ❌ API URL SAFETY CHECK FAILED');
        setError(text.systemConfigError);
        return;
      }
      
      console.error('[DriverOrders] fetchOrders error:', err.response?.status, err.message);
      
      // 401会被拦截器自动处理，这里只处理其他错误
      if (err.response?.status === 401) {
        setError(text.loginExpired);
        // 拦截器会自动触发登出
      } else if (err.response?.status === 403) {
        setError(text.accountDisabled);
      } else if (!err.response) {
        const cached = loadCachedDriverOrders();
        if (cached?.orders) {
          setOrdersIfChanged(cached.orders);
          setUsingCachedData(true);
          setError('');
        } else {
          setError(text.networkError);
        }
      } else {
        setError(`${text.orderFetchFailed} (${err.response?.status || text.unknownError})`);
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout, refreshOfflineOrderPack, setOrdersIfChanged, text]);

  // 获取追加订单
  const fetchExtraSchedules = useCallback(async () => {
    try {
      const data = await DriverAPI.getExtraSchedules();
      console.log('[DriverOrders] Extra schedules count:', data?.length || 0);
      setExtraSchedulesIfChanged(data || []);
    } catch (err) {
      console.error('[DriverOrders] fetchExtraSchedules error:', err);
      // 不显示错误，追加订单是辅助功能
    }
  }, [setExtraSchedulesIfChanged]);

  const fetchOrderPool = useCallback(async () => {
    setOrderPoolLoading(true);
    try {
      const data = await DriverAPI.getOrderPool();
      setOrderPool(data?.orders || []);
      setOrderPoolMeta({
        driver_score: data?.driver_score ?? driverProfile?.driver_score ?? 500,
        can_accept_order_pool: data?.can_accept_order_pool !== false,
      });
    } catch (err) {
      console.error('[DriverOrders] fetchOrderPool error:', err);
    } finally {
      setOrderPoolLoading(false);
    }
  }, [driverProfile?.driver_score]);

  const fetchOnboarding = useCallback(async () => {
    try {
      const data = await DriverAPI.getOnboarding();
      setOnboarding(data);
      if (data?.personal_info) {
        setPersonalInfoForm((current) => ({ ...current, ...data.personal_info }));
      }
      if (data?.vehicle_info) {
        setVehicleInfoForm((current) => ({ ...current, ...normalizeVehicleInfoForForm(data.vehicle_info) }));
      }
      if (data?.vehicle_inspection) {
        setInspectionForm((current) => ({ ...current, ...data.vehicle_inspection }));
      }
      if (data?.document_management) {
        setDocumentManagementForm((current) => ({ ...current, ...data.document_management }));
      }
      if (data?.steps?.training?.status === 'approved') {
        setTrainingConfirmedLessons((current) => {
          if (Object.keys(current || {}).length) return current;
          return Object.fromEntries((trainingLessonsRef.current || []).map((lesson) => [lesson.id, { confirmed_at: data?.training?.passed_at || true }]));
        });
      }
      if (data?.training?.confirmed_lessons) {
        setTrainingConfirmedLessons(data.training.confirmed_lessons);
      }
      if (data?.training?.last_wrong_questions) {
        setTrainingWrongQuestions(data.training.last_wrong_questions);
      }
    } catch (err) {
      console.error('[DriverOrders] fetchOnboarding error:', err);
    }
  }, []);

  const fetchTrainingQuestions = useCallback(async () => {
    try {
      const data = await DriverAPI.getTrainingProgram(driverLanguage);
      setTrainingLessons(data?.lessons?.length ? data.lessons : getDriverFeatureText(driverLanguage).trainingFallback);
      setTrainingQuestions(data?.questions || []);
      setTrainingConfirmedLessons(data?.progress?.confirmed_lessons || {});
      setTrainingWrongQuestions(data?.progress?.last_wrong_questions || []);
    } catch (err) {
      console.error('[DriverOrders] fetchTrainingQuestions error:', err);
      setTrainingLessons(getDriverFeatureText(driverLanguage).trainingFallback);
      const data = await DriverAPI.getTrainingQuestions().catch(() => []);
      setTrainingQuestions(data || []);
    }
  }, [driverLanguage]);

  const handleConfirmTrainingLesson = async () => {
    if (!currentTrainingLesson?.id) return;
    setOnboardingSaving('training_lesson');
    try {
      const data = await DriverAPI.confirmTrainingPage({
        lesson_id: currentTrainingLesson.id,
        language: driverLanguage,
      });
      setOnboarding(data);
      setTrainingConfirmedLessons(data?.training?.confirmed_lessons || {
        ...trainingConfirmedLessons,
        [currentTrainingLesson.id]: { confirmed_at: true, language: driverLanguage },
      });
      if (trainingLessonIndex < trainingLessons.length - 1) {
        setTrainingLessonIndex((current) => Math.min(current + 1, trainingLessons.length - 1));
      }
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setOnboardingSaving('');
    }
  };

  const fetchVehicleInspectionOptions = useCallback(async () => {
    try {
      const data = await DriverAPI.getVehicleInspectionOptions();
      setInspectionOptions(data || null);
      const siteOptions = Array.isArray(data?.sites) ? data.sites : [];
      const defaultSiteId = siteOptions.find((site) => site?.enabled !== false)?.id || data?.site?.id || '';
      if (defaultSiteId) {
        setInspectionForm((current) => {
          const currentSiteId = current.inspection_site_id || '';
          const currentIsValid = siteOptions.some((site) => site?.id === currentSiteId && site?.enabled !== false);
          return { ...current, inspection_site_id: currentIsValid ? currentSiteId : defaultSiteId };
        });
      }
    } catch (err) {
      console.error('[DriverOrders] fetchVehicleInspectionOptions error:', err);
    }
  }, []);

  const fetchVehicleInspectionPaymentConfig = useCallback(async () => {
    try {
      const data = await DriverAPI.getVehicleInspectionPaymentConfig();
      setSquarePaymentConfig(data || null);
      setSquarePaymentError('');
    } catch (err) {
      console.error('[DriverOrders] fetchVehicleInspectionPaymentConfig error:', err);
      setSquarePaymentConfig(null);
      setSquarePaymentError(getLocalizedDriverApiError(err, text.paymentSetupRequired, driverLanguage));
    }
  }, [driverLanguage, text.paymentSetupRequired]);

  const handleApplyOrderPool = async (order) => {
    if (!order?.id || orderPoolActionId) return;
    setOrderPoolActionId(order.id);
    try {
      const result = await DriverAPI.applyOrderPool(order.id, {
        client_event_id: `pool_${order.id}_${Date.now()}`,
      });
      await fetchOrderPool();
      if (result?.assigned) {
        await fetchOrders();
        setActiveDriverTab('orders');
        setRetryNotice(text.applySuccess);
        window.setTimeout(() => setRetryNotice(''), 2200);
      }
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setOrderPoolActionId(null);
    }
  };

  const handleCancelOrderPoolApplication = async (order) => {
    if (!order?.id || orderPoolActionId) return;
    setOrderPoolActionId(order.id);
    try {
      await DriverAPI.cancelOrderPoolApplication(order.id);
      await fetchOrderPool();
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.cancelFailed, driverLanguage));
    } finally {
      setOrderPoolActionId(null);
    }
  };

  const handlePersonalFile = async (key, file) => {
    try {
      const document = await readOnboardingFile(file);
      setPersonalFiles((current) => ({ ...current, [key]: document }));
    } catch (error) {
      alert(error?.message === 'ONBOARDING_FILE_TOO_LARGE' ? text.onboardingFileTooLarge : text.onboardingFileReadFailed);
    }
  };

  const handleProfilePhotoChange = async (file) => {
    if (!file) return;
    const document = await readOnboardingFile(file);
    if (!document) return;
    setPersonalFiles((current) => ({ ...current, profile_photo: document }));
    if (isFleetMasterAccount) {
      setOnboardingSaving('profile_photo');
      try {
        const data = await DriverAPI.updateProfilePhoto(document);
        setOnboarding(data);
      } catch (error) {
        alert(getLocalizedDriverApiError(error, featureText.avatarSaveFailed, driverLanguage));
      } finally {
        setOnboardingSaving('');
      }
    }
    setShowProfilePhotoMenu(false);
  };

  const openFleetManagement = (tab) => navigate(`/fleet/manage?tab=${tab}`);
  const openFleetUpcomingOrders = () => {
    setFilter('upcoming');
    setProfileSection('');
    setActiveDriverTab('orders');
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const togglePersonalServiceLanguage = (language) => {
    setPersonalInfoForm((current) => {
      const selected = new Set(current.service_languages || []);
      if (selected.has(language)) {
        selected.delete(language);
      } else {
        selected.add(language);
      }
      return { ...current, service_languages: Array.from(selected) };
    });
  };

  const handleVehicleFile = async (key, file) => {
    try {
      const document = await readOnboardingFile(file);
      setVehicleFiles((current) => ({ ...current, [key]: document }));
    } catch (error) {
      alert(error?.message === 'ONBOARDING_FILE_TOO_LARGE' ? text.onboardingFileTooLarge : text.onboardingFileReadFailed);
    }
  };

  const handleDocumentManagementFile = async (key, file) => {
    try {
      const document = await readOnboardingFile(file);
      setDocumentManagementFiles((current) => ({ ...current, [key]: document }));
    } catch (error) {
      alert(error?.message === 'ONBOARDING_FILE_TOO_LARGE' ? text.onboardingFileTooLarge : text.onboardingFileReadFailed);
    }
  };

  const submitPersonalInfo = async () => {
    const documents = {
      residence_card_front: personalFiles.residence_card_front || onboarding?.personal_info?.residence_card_front,
      residence_card_back: personalFiles.residence_card_back || onboarding?.personal_info?.residence_card_back,
      driver_license_front: personalFiles.driver_license_front || onboarding?.personal_info?.driver_license_front,
      driver_license_back: personalFiles.driver_license_back || onboarding?.personal_info?.driver_license_back,
    };
    const missingFields = [];
    if (!String(personalInfoForm.full_name || '').trim()) missingFields.push(text.fullName);
    if (!String(personalInfoForm.driver_license_expiry || '').trim()) missingFields.push(text.driverLicenseExpiry);
    if (!documents.driver_license_front) missingFields.push(text.driverLicenseFront);
    if (!documents.driver_license_back) missingFields.push(text.driverLicenseBack);
    if (personalInfoForm.is_japanese_national !== true) {
      if (!String(personalInfoForm.residence_card_number || '').trim()) missingFields.push(text.residenceCardNumber);
      if (!personalInfoForm.residence_card_long_term_valid && !String(personalInfoForm.residence_card_expiry || '').trim()) missingFields.push(text.residenceCardExpiry);
      if (!documents.residence_card_front) missingFields.push(text.residenceCardFront);
      if (!documents.residence_card_back) missingFields.push(text.residenceCardBack);
    }
    if (missingFields.length > 0) {
      alert(`${text.onboardingMissingFields}: ${missingFields.join(' / ')}`);
      return;
    }
    setOnboardingSaving('personal_info');
    try {
      const data = await DriverAPI.submitPersonalInfo({
        ...personalInfoForm,
        residence_card_front: personalInfoForm.is_japanese_national ? null : documents.residence_card_front,
        residence_card_back: personalInfoForm.is_japanese_national ? null : documents.residence_card_back,
        driver_license_front: documents.driver_license_front,
        driver_license_back: documents.driver_license_back,
        profile_photo: personalFiles.profile_photo || onboarding?.personal_info?.profile_photo,
      });
      setOnboarding(data);
      setRetryNotice(text.onboardingSubmitted);
      window.setTimeout(() => setRetryNotice(''), 2200);
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setOnboardingSaving('');
    }
  };

  const submitVehicleInfo = async () => {
    setOnboardingSaving('vehicle_info');
    try {
      const data = await DriverAPI.submitVehicleInfo({
        ...vehicleInfoForm,
        plate_number: buildPlateNumberFromParts(vehicleInfoForm),
        vehicle_model: vehicleInfoForm.vehicle_model,
        vehicle_color: vehicleInfoForm.vehicle_color,
        vehicle_year: Number(vehicleInfoForm.vehicle_year),
        optional_insurance_company: vehicleInfoForm.optional_insurance_company,
        optional_insurance_policy_number: vehicleInfoForm.optional_insurance_policy_number,
        optional_insurance_expiry: vehicleInfoForm.optional_insurance_expiry,
        compulsory_insurance_expiry: vehicleInfoForm.compulsory_insurance_expiry,
        inspection_certificate: vehicleFiles.inspection_certificate || onboarding?.vehicle_info?.inspection_certificate,
        record_certificate: vehicleFiles.record_certificate || onboarding?.vehicle_info?.record_certificate,
        optional_insurance_front: vehicleFiles.optional_insurance_front || onboarding?.vehicle_info?.optional_insurance_front,
        compulsory_insurance_photo: vehicleFiles.compulsory_insurance_photo || onboarding?.vehicle_info?.compulsory_insurance_photo,
      });
      setOnboarding(data);
      setRetryNotice(text.onboardingSubmitted);
      window.setTimeout(() => setRetryNotice(''), 2200);
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setOnboardingSaving('');
    }
  };

  const submitTraining = async () => {
    setOnboardingSaving('training');
    try {
      const data = await DriverAPI.submitTraining({ answers: trainingAnswers, language: driverLanguage });
      setOnboarding(data);
      setTrainingWrongQuestions([]);
      setRetryNotice(text.trainingPassed);
      window.setTimeout(() => setRetryNotice(''), 2200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.wrong_questions) {
        setTrainingWrongQuestions(detail.wrong_questions || []);
        const reviewLessonId = detail.review_lesson_ids?.[0];
        const reviewIndex = trainingLessons.findIndex((lesson) => lesson.id === reviewLessonId);
        if (reviewIndex >= 0) {
          setTrainingLessonIndex(reviewIndex);
        }
        alert(text.trainingWrongHint);
      } else {
        alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
      }
    } finally {
      setOnboardingSaving('');
    }
  };

  const submitVehicleInspection = async () => {
    setOnboardingSaving('vehicle_inspection');
    try {
      const data = await DriverAPI.submitVehicleInspection(inspectionForm);
      setOnboarding(data);
      setInspectionStep('payment');
      setRetryNotice(text.onboardingSubmitted);
      window.setTimeout(() => setRetryNotice(''), 2200);
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setOnboardingSaving('');
    }
  };

  const startVehicleInspectionPayment = async () => {
    setOnboardingSaving('vehicle_inspection_payment');
    try {
      const data = await DriverAPI.createVehicleInspectionPayment({ return_url: window.location.href });
      setOnboarding(data);
      const paymentUrl = data?.vehicle_inspection?.square_payment_url;
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else if (data?.vehicle_inspection?.payment_status === 'payment_setup_required') {
        alert(text.paymentSetupRequired);
      } else if (data?.vehicle_inspection?.payment_status === 'payment_link_failed') {
        alert(text.paymentLinkFailed);
      }
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setOnboardingSaving('');
    }
  };

  const submitVehicleInspectionCardPayment = async () => {
    if (!squareCardRef.current || squarePaymentProcessing) {
      return;
    }
    setSquarePaymentProcessing(true);
    setSquarePaymentError('');
    try {
      const tokenResult = await squareCardRef.current.tokenize({
        amount: String(squarePaymentConfig?.fee_jpy || inspectionOptions?.fee_jpy || 5000),
        currencyCode: squarePaymentConfig?.currency || 'JPY',
        intent: 'CHARGE',
        customerInitiated: true,
        sellerKeyedIn: false,
        billingContact: {
          givenName: profileDriverName,
          phone: profileDriverPhone,
        },
      });
      if (tokenResult.status !== 'OK') {
        throw new Error(tokenResult.errors?.[0]?.message || `Tokenization failed: ${tokenResult.status}`);
      }
      const idempotencyKey = window.crypto?.randomUUID ? window.crypto.randomUUID() : `driver-card-${Date.now()}`;
      const data = await DriverAPI.createVehicleInspectionCardPayment({
        source_id: tokenResult.token,
        idempotency_key: idempotencyKey,
      });
      setOnboarding(data);
      const status = String(data?.vehicle_inspection?.payment_status || '').toLowerCase();
      if (['paid', 'completed'].includes(status)) {
        setInspectionStep('qr');
      }
    } catch (err) {
      const message = getLocalizedDriverApiError(err, text.submitFailed, driverLanguage);
      setSquarePaymentError(message);
      alert(message);
    } finally {
      setSquarePaymentProcessing(false);
    }
  };

  const submitDocumentManagement = async () => {
    setOnboardingSaving('document_management');
    try {
      const currentDocuments = onboarding?.document_management || {};
      const payload = {
        ...documentManagementForm,
        driver_license_front: documentManagementFiles.driver_license_front || currentDocuments.driver_license_front,
        driver_license_back: documentManagementFiles.driver_license_back || currentDocuments.driver_license_back,
        inspection_certificate: documentManagementFiles.inspection_certificate || currentDocuments.inspection_certificate,
        record_certificate: documentManagementFiles.record_certificate || currentDocuments.record_certificate,
        optional_insurance_front: documentManagementFiles.optional_insurance_front || currentDocuments.optional_insurance_front,
        optional_insurance_back: documentManagementFiles.optional_insurance_back || currentDocuments.optional_insurance_back,
        residence_card_front: documentManagementForm.is_japanese_national ? null : (documentManagementFiles.residence_card_front || currentDocuments.residence_card_front),
        residence_card_back: documentManagementForm.is_japanese_national ? null : (documentManagementFiles.residence_card_back || currentDocuments.residence_card_back),
      };
      const data = await DriverAPI.submitDocumentManagement(payload);
      setOnboarding(data);
      setDocumentManagementFiles({});
      setRetryNotice(text.documentSaved);
      window.setTimeout(() => setRetryNotice(''), 2200);
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setOnboardingSaving('');
    }
  };

  const fetchMonthlyStats = useCallback(async (month = monthlyStatsMonth) => {
    if (!canViewRevenueStats) {
      setMonthlyStats(null);
      return;
    }
    setMonthlyStatsLoading(true);
    try {
      const data = await DriverAPI.getMonthlyStats(month);
      setMonthlyStats(data);
    } catch (err) {
      console.error('[DriverOrders] fetchMonthlyStats error:', err);
      setMonthlyStats(null);
    } finally {
      setMonthlyStatsLoading(false);
    }
  }, [canViewRevenueStats, monthlyStatsMonth]);

  const fetchScoreEvents = useCallback(async () => {
    setScoreEventsLoading(true);
    try {
      const data = await DriverAPI.getScoreEvents(50);
      setScoreEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (err) {
      console.error('[DriverOrders] fetchScoreEvents error:', err);
      setScoreEvents([]);
    } finally {
      setScoreEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchExtraSchedules();
    fetchOrderPool();
    fetchOnboarding();
    fetchTrainingQuestions();
    fetchVehicleInspectionOptions();
    fetchVehicleInspectionPaymentConfig();
  }, [fetchOrders, fetchExtraSchedules, fetchOrderPool, fetchOnboarding, fetchTrainingQuestions, fetchVehicleInspectionOptions, fetchVehicleInspectionPaymentConfig]);

  useEffect(() => {
    if (location.state?.driverChatListReturn === true) {
      setActiveDriverTab('conversations');
      fetchOrders();
    }
  }, [fetchOrders, location.state?.driverChatListReturn]);

  useEffect(() => {
    let syncing = false;
    let noticeTimer = null;

    const syncQueuedStatuses = async () => {
      if (syncing) return;
      const summary = getDriverStatusQueueSummary(offlineOrderPackDriverId);
      if (!summary.retryable) return;
      syncing = true;
      try {
        const result = await flushDriverStatusQueue((orderId, status, payload) => (
          DriverAPI.updateOrderStatus(orderId, status, payload)
        ), offlineOrderPackDriverId);
        if (result.flushed > 0) {
          setRetryNotice(text.retryPending);
          fetchOrders();
          window.clearTimeout(noticeTimer);
          noticeTimer = window.setTimeout(() => setRetryNotice(''), 2200);
        }
      } catch {
        // The queue keeps the failed item and retry metadata; the next online tick retries it.
      } finally {
        syncing = false;
      }
    };

    syncQueuedStatuses();
    window.addEventListener('online', syncQueuedStatuses);
    window.addEventListener(DRIVER_STATUS_QUEUE_CHANGED_EVENT, syncQueuedStatuses);
    const interval = window.setInterval(syncQueuedStatuses, 15000);

    return () => {
      window.removeEventListener('online', syncQueuedStatuses);
      window.removeEventListener(DRIVER_STATUS_QUEUE_CHANGED_EVENT, syncQueuedStatuses);
      window.clearInterval(interval);
      window.clearTimeout(noticeTimer);
    };
  }, [fetchOrders, offlineOrderPackDriverId, text.retryPending]);

  useEffect(() => {
    const driverChatPollingInterval = window.setInterval(() => {
      fetchOrders();
      fetchExtraSchedules();
      fetchOrderPool();
    }, orderPoolRefreshIntervalMs);
    return () => window.clearInterval(driverChatPollingInterval);
  }, [fetchOrders, fetchExtraSchedules, fetchOrderPool, orderPoolRefreshIntervalMs]); // driver-chat-polling-interval

  useEffect(() => {
    if (canViewRevenueStats) {
      fetchMonthlyStats(monthlyStatsMonth);
    } else {
      setMonthlyStats(null);
    }
  }, [canViewRevenueStats, fetchMonthlyStats, monthlyStatsMonth]);

  useEffect(() => {
    if (showScoreDetails) {
      fetchScoreEvents();
    }
  }, [fetchScoreEvents, showScoreDetails]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      fetchDriverProfile();
      fetchOrders();
    }, orderPoolRefreshIntervalMs);
    return () => window.clearInterval(timer);
  }, [fetchDriverProfile, fetchOrders, orderPoolRefreshIntervalMs]);

  // 打开追加订单编辑
  const openEditExtra = (e, schedule) => {
    e.stopPropagation();
    setEditingExtra(schedule);
    setShowExtraModal(true);
  };

  // 取消追加订单
  const handleCancelExtra = async (schedule) => {
    if (!window.confirm(`${text.cancelExtraConfirm}\n${schedule.date} ${schedule.time}\n${schedule.pickup_location} → ${schedule.dropoff_location}`)) {
      return;
    }
    
    setCancellingExtra(schedule.id);
    try {
      await DriverAPI.cancelExtraSchedule(schedule.id);
      fetchExtraSchedules();
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.cancelFailed, driverLanguage));
    } finally {
      setCancellingExtra(null);
    }
  };

  // Safari 兼容 - 使用原生 DOM API 填充 select 选项
  useEffect(() => {
    if (showReportModal && selectRef.current) {
      const select = selectRef.current;
      // 清空现有选项
      select.innerHTML = '';
      
      // 添加默认选项
      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.text = text.selectEndTime;
      select.appendChild(defaultOption);
      
      // 添加时间选项
      TIME_OPTIONS.forEach(time => {
        const option = document.createElement('option');
        option.value = time;
        option.text = time;
        select.appendChild(option);
      });
      
      // 设置当前值
      if (reportEndTime) {
        select.value = reportEndTime;
      }
    }
  }, [showReportModal, reportEndTime, text]);

  // 打开汇报弹窗
  const openReportModal = (e, order) => {
    e.stopPropagation(); // 阻止点击事件冒泡到订单卡片
    setSelectedOrder(order);
    setReportEndTime(order.reported_end_time || '');
    setShowReportModal(true);
  };

  // 提交汇报时间
  const handleReportEndTime = async () => {
    if (!reportEndTime || !selectedOrder) {
      alert(text.selectEndTime);
      return;
    }
    
    setReporting(true);
    try {
      console.log('[DriverOrders] Reporting end time via DriverAPI...');
      
      // 使用集中式DriverAPI
      await DriverAPI.reportEndTime(selectedOrder.id, reportEndTime);
      
      alert(text.reportSuccess);
      setShowReportModal(false);
      setSelectedOrder(null);
      fetchOrders(); // 刷新订单数据
    } catch (err) {
      if (err.message && err.message.includes('Safety Check Failed')) {
        alert(text.systemConfigError);
      } else {
        alert(getLocalizedDriverApiError(err, text.reportFailed, driverLanguage));
      }
    } finally {
      setReporting(false);
    }
  };

  const openFleetDriverAssignment = async (event, order) => {
    event.stopPropagation();
    setFleetDispatchOrder(order);
    setFleetDispatchSelectedDriver(null);
    setFleetDispatchStep('driver');
    setFleetDispatchVehicleDraft({ vehicle_type: 'alphard', plate_number: '' });
    setFleetDispatchLoading(true);
    try {
      const [drivers, vehicles] = await Promise.all([
        DriverAPI.getFleetDrivers(),
        DriverAPI.getFleetVehicles(),
      ]);
      setFleetDispatchDrivers((drivers || []).filter(isFleetDispatchableDriver));
      setFleetDispatchVehicles(vehicles || []);
    } catch (err) {
      setFleetDispatchOrder(null);
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setFleetDispatchLoading(false);
    }
  };

  const closeFleetDriverAssignment = (force = false) => {
    if (fleetDispatchSaving && !force) return;
    setFleetDispatchOrder(null);
    setFleetDispatchSelectedDriver(null);
    setFleetDispatchStep('driver');
    setFleetDispatchVehicleDraft({ vehicle_type: 'alphard', plate_number: '' });
  };

  const chooseFleetDriver = (targetDriver) => {
    if (fleetDispatchSaving) return;
    setFleetDispatchSelectedDriver(targetDriver);
    setFleetDispatchStep('vehicle');
  };

  const assignFleetOrderDriver = async (targetVehicle) => {
    if (!fleetDispatchOrder || !fleetDispatchSelectedDriver || fleetDispatchSaving) return;

    setFleetDispatchSaving(targetVehicle.id);
    try {
      await DriverAPI.assignFleetOrderDriver(
        fleetDispatchOrder.id,
        fleetDispatchSelectedDriver.id,
        targetVehicle.id,
      );
      closeFleetDriverAssignment(true);
      setRetryNotice(`${text.fleetAssignedDriver}: ${fleetDispatchSelectedDriver.name}`);
      window.setTimeout(() => setRetryNotice(''), 2200);
      await fetchOrders();
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setFleetDispatchSaving('');
    }
  };

  const saveAndAssignFleetVehicle = async (event) => {
    event.preventDefault();
    if (!fleetDispatchOrder || !fleetDispatchSelectedDriver || fleetDispatchSaving) return;

    setFleetDispatchSaving('new-vehicle');
    try {
      const saved = await DriverAPI.saveFleetVehicle(fleetDispatchVehicleDraft);
      const vehicle = saved?.vehicle;
      if (!vehicle?.id) throw new Error('vehicle_save_failed');
      setFleetDispatchVehicles((current) => [
        vehicle,
        ...current.filter((item) => item.id !== vehicle.id),
      ]);
      await DriverAPI.assignFleetOrderDriver(
        fleetDispatchOrder.id,
        fleetDispatchSelectedDriver.id,
        vehicle.id,
      );
      closeFleetDriverAssignment(true);
      setRetryNotice(`${text.fleetAssignedDriver}: ${fleetDispatchSelectedDriver.name}`);
      window.setTimeout(() => setRetryNotice(''), 2200);
      await fetchOrders();
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setFleetDispatchSaving('');
    }
  };

  const deleteFleetVehicle = async (event, vehicle) => {
    event.stopPropagation();
    if (fleetDispatchSaving || !window.confirm(`${text.fleetDeleteVehicle} ${vehicle.plate_number || ''}?`)) return;

    setFleetDispatchSaving(`delete-${vehicle.id}`);
    try {
      await DriverAPI.deleteFleetVehicle(vehicle.id);
      setFleetDispatchVehicles((current) => current.filter((item) => item.id !== vehicle.id));
      setRetryNotice(text.fleetVehicleDeleted);
      window.setTimeout(() => setRetryNotice(''), 2200);
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setFleetDispatchSaving('');
    }
  };

  const handleDownloadSignboardPdf = async (e, order) => {
    e.stopPropagation();
    setGeneratingSignboardOrderId(order.id);
    try {
      const result = await DriverAPI.createSignboardPdfDownloadLink(order.id);
      await openDriverPdfDownloadUrl(result.download_url);
    } catch (err) {
      if (err.message && err.message.includes('Safety Check Failed')) {
        alert(text.systemConfigError);
      } else if (!err.response) {
        const cachedPdf = await loadCachedDriverOfflineSignboardPdf(offlineOrderPackDriverId, order.id);
        if (cachedPdf) {
          openCachedDriverOfflineSignboardPdf(cachedPdf);
        } else {
          alert(text.offlineSignboardUnavailable);
        }
      } else {
        alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
      }
    } finally {
      setGeneratingSignboardOrderId(null);
    }
  };

  const handleDownloadExtraSignboardPdf = async (event, schedule) => {
    event.stopPropagation();
    if (!schedule.customer_name?.trim()) { alert(extraCopy.signboardNameRequired); return; }
    setGeneratingSignboardOrderId(schedule.id);
    try {
      const result = await DriverAPI.createExtraSignboardPdfDownloadLink(schedule.id);
      await openDriverPdfDownloadUrl(resolveDriverDownloadUrl(result.download_url));
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally { setGeneratingSignboardOrderId(null); }
  };

  const openCalendarDownloadUrl = (downloadUrl) => {
    window.location.assign(resolveDriverDownloadUrl(downloadUrl));
  };

  const handleAddOrderToCalendar = async (e, order) => {
    e.stopPropagation();
    try {
      await addDriverOrderToCalendar(order, {
        createCalendarDownloadLink: DriverAPI.createOrderCalendarDownloadLink,
        openCalendarUrl: openCalendarDownloadUrl,
        resolveDownloadUrl: resolveDriverDownloadUrl,
        language: driverLanguage,
      });
    } catch (err) {
      alert(getCalendarErrorMessage(err, text));
    }
  };

  const fetchDriverNotices = async () => {
    setDriverNoticesLoading(true);
    setError('');
    try {
      const notices = await DriverAPI.getDriverNotices();
      setDriverNotices(Array.isArray(notices) ? notices : []);
      setShowNoticesModal(true);
    } catch (err) {
      setError(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setDriverNoticesLoading(false);
    }
  };

  const todayString = useMemo(() => getTokyoDateKey(), []);

  // 过滤订单
  const filteredOrders = useMemo(() => orders.filter(order => {
    if (filter === 'upcoming') {
      return order.departure_date >= todayString
        && order.status !== 'cancelled'
        && !isDriverOrderCompleted(order);
    } else if (filter === 'completed') {
      return isDriverOrderCompleted(order) || order.departure_date < todayString;
    }
    return true;
  }), [filter, orders, todayString]);
  const chatConversations = useMemo(() => (
    orders
      .filter((order) => order.trip_chat_available)
      .sort((left, right) => {
        const leftTime = left.trip_chat_last_message_at || `${left.departure_date || ''}T${left.pickup_time || '00:00'}`;
        const rightTime = right.trip_chat_last_message_at || `${right.departure_date || ''}T${right.pickup_time || '00:00'}`;
        return String(rightTime).localeCompare(String(leftTime));
      })
  ), [orders]);
  const activeChatConversations = useMemo(() => (
    chatConversations.filter((order) => !isDriverChatCompletedByOrderTime(order, chatClock))
  ), [chatClock, chatConversations]);
  const completedChatConversations = useMemo(() => (
    chatConversations.filter((order) => isDriverChatCompletedByOrderTime(order, chatClock))
  ), [chatClock, chatConversations]);
  const visibleChatConversations = chatCategory === 'completed'
    ? completedChatConversations
    : activeChatConversations;
  const unreadChatCount = chatConversations.reduce((total, order) => (
    total + getDriverUnreadMessageCount(order)
  ), 0);

  const nextDriverOrder = useMemo(() => {
    return orders
      .filter((order) => (
        order.departure_date >= todayString
        && order.status !== 'cancelled'
        && !isDriverOrderCompleted(order)
      ))
      .sort((a, b) => `${a.departure_date || ''} ${a.pickup_time || ''}`.localeCompare(`${b.departure_date || ''} ${b.pickup_time || ''}`))[0] || null;
  }, [orders, todayString]);

  useEffect(() => {
    document.title = unreadChatCount > 0 ? `${text.unreadMessage} ${unreadChatCount} - ${text.myOrders}` : text.myOrders;
  }, [unreadChatCount, text]);

  // 过滤追加订单
  const filteredExtraSchedules = useMemo(() => extraSchedules.filter(schedule => {
    if (filter === 'upcoming') {
      return schedule.date >= todayString && schedule.status === 'active';
    } else if (filter === 'completed') {
      return schedule.date < todayString || schedule.status === 'cancelled';
    }
    return true;
  }), [extraSchedules, filter, todayString]);

  // 合并正式订单和追加订单，按日期分组
  const { groupedItems, sortedDates } = useMemo(() => {
    const nextGroupedItems = {};
    filteredOrders.forEach(order => {
      const date = order.departure_date;
      if (!nextGroupedItems[date]) {
        nextGroupedItems[date] = [];
      }
      nextGroupedItems[date].push({ ...order, _type: 'order' });
    });
    filteredExtraSchedules.forEach(schedule => {
      const date = schedule.date;
      if (!nextGroupedItems[date]) {
        nextGroupedItems[date] = [];
      }
      nextGroupedItems[date].push({ ...schedule, _type: 'extra' });
    });
    const nextSortedDates = Object.keys(nextGroupedItems).sort((a, b) => {
      if (filter === 'completed') {
        return b.localeCompare(a);
      }
      return a.localeCompare(b);
    });
    nextSortedDates.forEach(date => {
      nextGroupedItems[date].sort((a, b) => {
        const timeA = a._type === 'order' ? (a.pickup_time || '') : (a.time || '');
        const timeB = b._type === 'order' ? (b.pickup_time || '') : (b.time || '');
        return timeA.localeCompare(timeB);
      });
    });
    return { groupedItems: nextGroupedItems, sortedDates: nextSortedDates };
  }, [filter, filteredExtraSchedules, filteredOrders]);

  const formatDate = (dateStr) => formatDriverDate(dateStr);
  const currentDocuments = onboarding?.document_management || {};
  const hasDocumentValue = (value) => Boolean(String(value || '').trim());
  const hasDocumentFile = (value) => Boolean(value && (value.data_url || value.url || value.name || value.id));
  const documentPersonalComplete = Boolean(
    hasDocumentValue(currentDocuments.full_name)
    && hasDocumentValue(currentDocuments.address)
    && hasDocumentValue(currentDocuments.driver_license_number)
    && hasDocumentValue(currentDocuments.driver_license_expiry)
    && hasDocumentFile(currentDocuments.driver_license_front)
    && hasDocumentFile(currentDocuments.driver_license_back)
    && (
      currentDocuments.is_japanese_national === true
      || (
        hasDocumentValue(currentDocuments.residence_card_number)
        && (hasDocumentValue(currentDocuments.residence_card_expiry) || currentDocuments.residence_card_long_term_valid === true)
        && hasDocumentFile(currentDocuments.residence_card_front)
        && hasDocumentFile(currentDocuments.residence_card_back)
      )
    )
  );
  const documentVehicleComplete = Boolean(
    hasDocumentValue(currentDocuments.vehicle_inspection_expiry)
    && hasDocumentFile(currentDocuments.inspection_certificate)
    && hasDocumentFile(currentDocuments.record_certificate)
    && hasDocumentValue(currentDocuments.optional_insurance_company)
    && hasDocumentValue(currentDocuments.optional_insurance_policy_number)
    && hasDocumentValue(currentDocuments.optional_insurance_expiry)
    && hasDocumentFile(currentDocuments.optional_insurance_front)
    && hasDocumentFile(currentDocuments.optional_insurance_back)
  );
  const documentBankComplete = Boolean(
    hasDocumentValue(currentDocuments.bank_name)
    && hasDocumentValue(currentDocuments.bank_branch)
    && hasDocumentValue(currentDocuments.bank_account_type)
    && hasDocumentValue(currentDocuments.bank_account_number)
    && hasDocumentValue(currentDocuments.bank_account_holder)
  );
  const renderDocumentGroupStatus = (complete) => (
    complete
      ? <span className="driver-profile-approved-badge" aria-label={text.completed}>✓</span>
      : <span className="driver-profile-pending-badge">{text.needsCompletion}</span>
  );
  const formatProfileSettlementYen = (amount) => {
    const value = Number(amount);
    return `¥${Number.isFinite(value) ? Math.round(value).toLocaleString() : 0}`;
  };
  const getWalletPaymentStatus = (order) => {
    const raw = String(
      order.driver_settlement_status
      || order.driver_payment_status
      || order.payout_status
      || order.payment_status
      || ''
    ).toLowerCase();
    if (order.driver_paid === true || order.paid === true || ['paid', 'settled', 'completed'].includes(raw)) {
      return { label: text.walletPaid, tone: 'paid' };
    }
    return { label: text.walletUnpaid, tone: 'unpaid' };
  };
  const getWalletDetailItems = () => {
    const statsOrders = Array.isArray(monthlyStats?.orders) ? monthlyStats.orders : [];
    return statsOrders.map((order) => {
      const orderAmount = order.driver_dispatch_price_visible === false
        ? null
        : Number(order.driver_dispatch_price_amount ?? order.dispatch_price ?? order.driver_amount ?? 0);
      const overtimeFee = Number(
        order.driver_overtime_fee
        ?? order.overtime_fee
        ?? order.overtime_price
        ?? order.extra_time_fee
        ?? order.extra_waiting_fee
        ?? 0
      );
      const payment = getWalletPaymentStatus(order);
      return {
        id: order.order_id || order.id || order.order_no,
        orderNo: order.order_no || order.order_id || order.id || '-',
        route: order.route_summary || order.customer_name || '-',
        date: [order.departure_date, order.pickup_time].filter(Boolean).join(' '),
        orderAmount: Number.isFinite(orderAmount) ? orderAmount : null,
        overtimeFee: Number.isFinite(overtimeFee) ? overtimeFee : 0,
        settlementDate: order.driver_settlement_date || profileSettlementTotals.next_month_settlement_date || '-',
        paymentLabel: payment.label,
        paymentTone: payment.tone,
      };
    });
  };
  const walletDetailItems = getWalletDetailItems();
  const getScoreEventLabel = (eventType) => text.scoreEventTypes?.[eventType] || eventType || text.scoreAdjustment;
  const scoreEventItems = (Array.isArray(scoreEvents) ? scoreEvents : []).map((event) => {
    const points = Number(event.points_delta || 0);
    return {
      id: event.id || `${event.order_id || 'score'}-${event.created_at}`,
      label: getScoreEventLabel(event.event_type),
      orderNo: event.order_no || event.order_id || '-',
      date: event.created_at ? new Date(event.created_at).toLocaleString() : '-',
      note: event.note || '',
      points,
      pointsLabel: `${points > 0 ? '+' : ''}${points}`,
      tone: points >= 0 ? 'positive' : 'negative',
      oldScore: event.old_score,
      newScore: event.new_score,
    };
  });
  const profileProgressSteps = [
    { key: 'personal_info', label: text.onboardingSteps?.personal_info || text.unknownStatus },
    { key: 'vehicle_info', label: text.onboardingSteps?.vehicle_info || text.unknownStatus },
    {
      key: 'training_vehicle',
      label: `${text.training || text.onboardingSteps?.training || text.unknownStatus} / ${text.vehicleInspectionDocument || text.onboardingSteps?.vehicle_inspection || text.unknownStatus}`,
      status: onboardingSteps.training?.status === 'approved' && onboardingSteps.vehicle_inspection?.status === 'approved' ? 'approved' : 'pending',
    },
    { key: 'final_review', label: text.onboardingSteps?.final_review || text.unknownStatus },
  ].map((step) => ({
    ...step,
    status: step.status || onboardingSteps[step.key]?.status || 'pending',
  }));
  const certificationSteps = [
    {
      key: 'personal_info',
      title: featureText.certification.personalTitle,
      subtitle: featureText.certification.personalSubtitle,
      pendingLabel: featureText.certification.personalPending,
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M8 8.5h3.2M8 12h8M8 15.5h5.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <circle cx="15.8" cy="8.8" r="1.6" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      ),
    },
    {
      key: 'vehicle_info',
      title: featureText.certification.vehicleTitle,
      subtitle: featureText.certification.vehicleSubtitle,
      pendingLabel: featureText.certification.vehiclePending,
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 14.5h16l-1.8-5.1A2 2 0 0 0 16.3 8H7.7a2 2 0 0 0-1.9 1.4L4 14.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
          <path d="M6 14.5V18h2M16 18h2v-3.5M8 18h8M7.5 12h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <circle cx="7.8" cy="15.7" r="0.7" fill="currentColor" />
          <circle cx="16.2" cy="15.7" r="0.7" fill="currentColor" />
        </svg>
      ),
    },
    {
      key: 'training',
      title: featureText.certification.trainingTitle,
      subtitle: featureText.certification.trainingSubtitle,
      pendingLabel: featureText.certification.trainingPending,
      mutedWhenPending: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 8.5 12 5l8 3.5-8 3.5L4 8.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
          <path d="M7 11v3.8c0 1.5 2.2 3 5 3s5-1.5 5-3V11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M20 9v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      key: 'vehicle_inspection',
      title: featureText.certification.inspectionTitle,
      subtitle: featureText.certification.inspectionSubtitle,
      pendingLabel: featureText.certification.inspectionPending,
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M8 4h8l1 2h2a1.5 1.5 0 0 1 1.5 1.5v11A1.5 1.5 0 0 1 19 20H5a1.5 1.5 0 0 1-1.5-1.5v-11A1.5 1.5 0 0 1 5 6h2l1-2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
          <path d="M8.2 12.2 10.5 14.5 16 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      key: 'payout_account',
      targetSection: 'document_bank',
      title: text.payoutAccountTitle,
      subtitle: text.payoutAccountHint,
      pendingLabel: text.needsCompletion,
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 10h16M6 10v8M10 10v8M14 10v8M18 10v8M5 18h14M12 4l8 4H4l8-4Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      key: 'final_review',
      title: featureText.certification.finalTitle,
      subtitle: featureText.certification.finalSubtitle,
      pendingLabel: featureText.certification.finalPending,
      lockedLabel: featureText.certification.finalPending,
      mutedWhenPending: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M6 11V8a6 6 0 0 1 12 0v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M5 11.5A1.5 1.5 0 0 1 6.5 10h11a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 17.5 20h-11A1.5 1.5 0 0 1 5 18.5v-7Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M12 14v2.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      ),
    },
  ];
  const onboardingCertificationStepKeys = ['personal_info', 'vehicle_info', 'payout_account', 'training', 'vehicle_inspection', 'final_review'];
  const certificationStepsByKey = certificationSteps.reduce((result, step) => ({
    ...result,
    [step.key]: step,
  }), {});
  const certificationCompleteCount = ['personal_info', 'vehicle_info', 'training', 'vehicle_inspection', 'final_review'].filter((step) => onboardingSteps[step]?.status === 'approved').length;
  const certificationProgressTotal = 5;
  const certificationProgressPercent = Math.round((certificationCompleteCount / certificationProgressTotal) * 100);
  const certificationPrerequisitesApproved = ['personal_info', 'vehicle_info', 'training', 'vehicle_inspection'].every((step) => (
    onboardingSteps[step]?.status === 'approved'
  ));
  const onboardingReviewStepKeys = ['personal_info', 'vehicle_info', 'training', 'vehicle_inspection', 'final_review'];
  const getCertificationRejectionNote = (stepKey) => {
    const step = onboardingSteps[stepKey] || {};
    return step.status === 'rejected' ? String(step.note || '').trim() : '';
  };
  const getCertificationStatusMeta = (step) => {
    if (step.key === 'payout_account') {
      return documentBankComplete
        ? { label: text.onboardingItemStatus?.completed || text.completed, tone: 'is-complete', note: '' }
        : { label: text.onboardingItemStatus?.to_complete || text.needsCompletion, tone: 'is-muted', note: '' };
    }
    const status = onboardingSteps[step.key]?.status || 'pending';
    const note = getCertificationRejectionNote(step.key);
    if (step.key === 'final_review' && !certificationPrerequisitesApproved && status === 'pending') {
      return { label: text.onboardingItemStatus?.not_started || step.lockedLabel || text.pending, tone: 'is-locked', note: '' };
    }
    if (status === 'approved') return { label: text.onboardingItemStatus?.completed || text.completed, tone: 'is-complete', note: '' };
    if (status === 'submitted') return { label: text.onboardingItemStatus?.reviewing || text.onboardingStatus?.submitted || text.pending, tone: 'is-reviewing', note: '' };
    if (status === 'rejected') return { label: text.onboardingItemStatus?.needs_changes || featureText.certification.needsChanges, tone: 'is-rejected', note };
    if (step.key === 'training') return { label: text.onboardingItemStatus?.incomplete || text.pending, tone: 'is-muted', note: '' };
    if (step.key === 'vehicle_inspection') return { label: text.onboardingItemStatus?.incomplete || text.pending, tone: '', note: '' };
    return { label: text.onboardingItemStatus?.to_complete || step.pendingLabel || text.needsCompletion, tone: step.mutedWhenPending ? 'is-muted' : '', note: '' };
  };
  const certificationOverallStatus = (() => {
    if (onboardingSteps.final_review?.status === 'approved') {
      return { key: 'approved', label: text.onboardingAuditStates?.approved || text.onboardingStatus?.approved, tone: 'is-complete' };
    }
    if (onboardingReviewStepKeys.some((step) => onboardingSteps[step]?.status === 'rejected')) {
      return { key: 'needs_changes', label: text.onboardingAuditStates?.needs_changes || featureText.certification.needsChanges, tone: 'is-rejected' };
    }
    if (onboardingReviewStepKeys.some((step) => onboardingSteps[step]?.status === 'submitted')) {
      return { key: 'reviewing', label: text.onboardingAuditStates?.reviewing || text.onboardingStatus?.submitted, tone: 'is-reviewing' };
    }
    return { key: 'pending_submission', label: text.onboardingAuditStates?.pending_submission || text.pending, tone: 'is-muted' };
  })();
  const renderOnboardingRejectionReason = (stepKey) => {
    const note = getCertificationRejectionNote(stepKey);
    if (!note) return null;
    return (
      <div className="driver-cert-rejection-note">
        <strong>{text.rejectionReason}</strong>
        <span>{note}</span>
      </div>
    );
  };
  const getCertificationStatusLabel = (step) => {
    return getCertificationStatusMeta(step).label;
  };
  const getCertificationStatusTone = (step) => {
    return getCertificationStatusMeta(step).tone;
  };
  const getDocumentUploadName = (key) => documentManagementFiles[key]?.name || currentDocuments?.[key]?.name;
  const renderOnboardingUploadControl = (key, label, onChange, selectedName) => (
    <label key={key} className="driver-onboarding-upload">
      <span>{label}</span>
      <input type="file" accept="image/*,.pdf" onChange={(e) => onChange(key, e.target.files?.[0])} />
      <span className="driver-onboarding-upload-meta">
        <span className="driver-onboarding-upload-action">{text.openAlbumOrChooseFile}</span>
        <span className="driver-onboarding-upload-status">
          {selectedName ? `${text.fileSelected}: ${selectedName}` : text.noFileSelected}
        </span>
      </span>
    </label>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
          <div className="text-muted-foreground">{text.loading}</div>
      </div>
    );
  }

  return (
    <div className={`driver-orders-page min-h-screen bg-surface pb-28 ${activeDriverTab === 'attendance' ? 'driver-attendance-page' : ''}`}>
      {/* Header */}
      {activeDriverTab !== 'attendance' && <DriverBrandHeader
        pageTitle={activeDriverTab === 'attendance' ? attendanceTitle : activeDriverTab === 'pool'
          ? text.orderPool
          : activeDriverTab === 'conversations'
            ? text.conversations
            : activeDriverTab === 'profile'
              ? text.myPage
              : text.myOrders}
        driverName={driver?.name}
      >
        {activeDriverTab === 'orders' && unreadChatCount > 0 && (
          <span className="driver-unread-badge">
            {text.unreadMessage} {unreadChatCount}
          </span>
        )}

        {/* Filter Tabs */}
        {activeDriverTab === 'orders' && (
          <>
            <DriverOrdersToolbar filter={filter} setFilter={setFilter} text={text} canRequestLeave={canRequestLeave} reportTitle={dailyText(getDriverLanguage()).title} onLeave={()=>setShowTimeOffModal(true)} onAdd={()=>{setEditingExtra(null);setShowExtraModal(true);}} onRefresh={()=>{fetchDriverProfile();fetchOrders();fetchExtraSchedules();fetchMonthlyStats(monthlyStatsMonth);}} onReport={()=>window.dispatchEvent(new Event("driver-open-daily-report"))}/>

          </>
        )}
      </DriverBrandHeader>}

      {/* Content */}
      <div className={`driver-orders-scroll p-4 ${activeDriverTab === 'conversations' ? 'driver-chat-list-scroll' : ''}`}>
        {(usingCachedData || retryNotice) && (
          <div className="mb-4 rounded-xl border border-[#d8b769]/30 bg-[#d8b769]/10 px-4 py-3 text-sm font-semibold text-[#f6d982]" aria-label={text.cachedData}>
            {usingCachedData ? text.cachedData : retryNotice}
          </div>
        )}

        {activeDriverTab === 'orders' && offlineOrderPackSummary.orderCount > 0 && (
          <div
            className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-[#5ee0b8]/25 bg-[#5ee0b8]/10 px-4 py-3 text-sm"
            data-testid="driver-offline-order-pack-status"
          >
            <span className="font-semibold text-[#74e1bf]">{text.offlineOrderPackReady(offlineOrderPackSummary.orderCount)}</span>
            <span className="shrink-0 text-xs text-[#a7f1da]">{text.offlineSignboardReady(offlineOrderPackSummary.pdfCount)}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 premium-chip-destructive px-3 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        {activeDriverTab === 'attendance' ? (
          <DriverAttendance enabled={attendanceEnabled} language={driverLanguage} driverName={profileDriverName} />
        ) : activeDriverTab === 'pool' ? (
          <div className="space-y-4 pb-24">
            <div className="driver-order-pool-summary">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-[#f6d982]">{text.poolOrders}</div>
                <div className="mt-1 text-sm text-muted-foreground">{text.poolDecisionHint}</div>
              </div>
            </div>
            {driverAnnouncement ? (
              <div className="rounded-2xl border border-[#f6d982]/20 bg-[#f6d982]/8 px-4 py-3 text-sm leading-6 text-[#f6d982]">
                {driverAnnouncement}
              </div>
            ) : null}
            <div className="driver-order-pool-stage">
              {!canAcceptOrderPool && orderPoolAccessMessage && (
                <div className="driver-order-pool-lock-overlay" role="status" aria-live="polite">
                  <span>{orderPoolAccessMessage}</span>
                </div>
              )}
              <div
                className={`driver-order-pool-content ${!canAcceptOrderPool && orderPoolAccessMessage && orderPool.length > 0 ? 'driver-order-pool-content-blurred' : ''}`}
                style={{
                  '--driver-order-pool-opacity': String(orderPoolOpacity),
                  '--driver-order-pool-gap': orderPoolGap,
                  '--driver-order-pool-card-padding': orderPoolCardPadding,
                }}
              >
                {orderPoolLoading && orderPool.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">{text.loading}</div>
                ) : orderPool.length === 0 ? (
                  <div className="whitespace-pre-line py-12 text-center leading-7 text-muted-foreground">{text.noPoolOrders}</div>
                ) : (
                  orderPool.map((poolOrder) => (
                    <div key={poolOrder.id} className="driver-order-pool-card" style={{ padding: 'var(--driver-order-pool-card-padding, 1.1rem)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-muted-foreground">{formatDate(poolOrder.departure_date)}</div>
                      <div className="mt-1 text-4xl font-black text-foreground">{poolOrder.pickup_time || '--:--'}</div>
                    </div>
                    <div className="driver-order-pool-price">
                      <span>{text.dispatchPriceLabel}</span>
                      <strong>{formatYen(poolOrder.dispatch_price)}</strong>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 text-success">●</span>
                      <span className="font-bold text-foreground">{formatDriverLocationLine(poolOrder.pickup_area, poolOrder.pickup_location, poolOrder.pickup_area)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 text-destructive">●</span>
                      <span className="font-bold text-foreground">{formatDriverLocationLine(poolOrder.dropoff_area, poolOrder.dropoff_location, poolOrder.dropoff_area)}</span>
                    </div>
                  </div>
                  <OrderSeatBadges order={poolOrder} language={driverLanguage} />
                  {(() => {
                    const extraServiceTags = buildDriverExtraServiceTags(poolOrder, text);
                    if (!extraServiceTags.length) return null;
                    return (
                      <div className="driver-order-pool-extra-tags" aria-label={text.extraServices}>
                        {extraServiceTags.map((tag) => (
                          <span key={tag} className="driver-order-pool-extra-tag">{tag}</span>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="mt-4 flex items-center justify-between gap-3 border-t border-border-subtle pt-3 text-sm">
                    <span className="premium-chip">{serviceTypeLabel(poolOrder.service_type)}{poolOrder.service_type === 'charter' && poolOrder.selected_hours ? ` · ${poolOrder.selected_hours}h` : ''}</span>
                    <span className="font-bold text-muted-foreground">{formatDriverVehicleType(poolOrder.vehicle_type)}</span>
                  </div>
                  <div className="mt-4">
                    {poolOrder.application_status === 'active' ? (
                      <button
                        type="button"
                        onClick={() => handleCancelOrderPoolApplication(poolOrder)}
                        disabled={orderPoolActionId === poolOrder.id}
                        className="driver-status-issue-button"
                      >
                        {orderPoolActionId === poolOrder.id ? text.submitting : `${text.applicationPending} · ${text.cancelApplication}`}
                      </button>
                    ) : (
                      <DriverPoolSwipeControl
                        label={text.applyOrder}
                        disabled={!canAcceptOrderPool || orderPoolActionId === poolOrder.id}
                        updating={orderPoolActionId === poolOrder.id}
                        onComplete={() => handleApplyOrderPool(poolOrder)}
                        text={text}
                      />
                    )}
                  </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : activeDriverTab === 'conversations' ? (
          <div className="driver-chat-conversation-list pb-24" data-testid="driver-chat-conversation-list">
            <div className="driver-chat-conversation-intro">
              <strong>{text.conversations}</strong>
              <span>{text.conversationListHint}</span>
            </div>
            <div className="driver-chat-category-tabs" role="tablist" aria-label={text.conversations} data-testid="driver-chat-category-tabs">
              <button
                type="button"
                role="tab"
                aria-selected={chatCategory === 'active'}
                className={`driver-chat-category-tab ${chatCategory === 'active' ? 'is-active' : ''}`}
                onClick={() => setChatCategory('active')}
                data-testid="driver-chat-category-active"
              >
                <span>{text.activeConversations}</span>
                <span className="driver-chat-category-count">{activeChatConversations.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={chatCategory === 'completed'}
                className={`driver-chat-category-tab ${chatCategory === 'completed' ? 'is-active' : ''}`}
                onClick={() => setChatCategory('completed')}
                data-testid="driver-chat-category-completed"
              >
                <span>{text.completedConversations}</span>
                <span className="driver-chat-category-count">{completedChatConversations.length}</span>
              </button>
            </div>
            {visibleChatConversations.length === 0 ? (
              <div className="driver-chat-conversation-empty">
                <span className="driver-chat-conversation-empty-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 10h8M8 14h5m8-2a9 9 0 11-4.7-7.9L21 3v5h-5" />
                  </svg>
                </span>
                <p>{chatCategory === 'completed' ? text.noCompletedConversations : text.noActiveConversations}</p>
              </div>
            ) : visibleChatConversations.map((order) => {
              const unreadCount = getDriverUnreadMessageCount(order);
              const conversationTitle = order.customer_name || order.order_no || text.unknownCustomer;
              const conversationInitial = Array.from(String(conversationTitle).trim())[0] || '•';
              const pickup = formatDriverLocationLine(
                order.pickup_area,
                order.pickup_location_detail || order.pickup_location,
                order.pickup_location
              );
              const dropoff = formatDriverLocationLine(
                order.dropoff_area,
                order.dropoff_location_detail || order.destination,
                order.destination
              );
              const isCharter = String(order.service_type || '').toLowerCase() === 'charter';
              const serviceSummary = `${serviceTypeLabel(order.service_type)}${isCharter && order.selected_hours ? ` · ${order.selected_hours}${text.hours}` : ''}`;
              const routeSummary = isCharter && (!dropoff || dropoff === '-' || dropoff === pickup)
                ? `${pickup} · ${serviceTypeLabel(order.service_type)}`
                : `${pickup} → ${dropoff}`;
              return (
                <button
                  key={order.id}
                  type="button"
                  className="driver-chat-conversation-row"
                  onClick={() => navigateFromOrders(`/driver/orders/${order.id}/chat`, { state: { driverChatListReturn: true } })}
                  data-testid={`driver-chat-conversation-${order.id}`}
                >
                  <span className="driver-chat-conversation-avatar" aria-hidden="true">
                    {conversationInitial}
                  </span>
                  <span className="driver-chat-conversation-copy">
                    <span className="driver-chat-conversation-title">
                      {conversationTitle}
                    </span>
                    <span className="driver-chat-conversation-meta">
                      {formatDriverDate(order.departure_date)} · {serviceSummary}
                    </span>
                    <span className="driver-chat-conversation-route">{routeSummary}</span>
                  </span>
                  <span className="driver-chat-conversation-side">
                    <span className="driver-chat-conversation-time">
                      {formatDriverConversationTimestamp(order.trip_chat_last_message_at, driverLanguage)}
                    </span>
                    {unreadCount > 0 && (
                      <span className="driver-chat-conversation-unread" aria-label={`${text.unreadMessage} ${unreadCount}`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        ) : activeDriverTab === 'profile' ? (
          <div className={`driver-profile-page ${profileSection ? 'driver-profile-subpage' : ''} pb-24`}> 
            <section className={`driver-profile-hero ${isFleetMasterAccount ? 'driver-profile-hero-fleet' : ''}`}>
              {isFleetMasterAccount ? <FleetMasterEmblem label={text.fleetMasterAccount} photoUrl={profilePhotoUrl} saving={onboardingSaving === 'profile_photo'} onClick={() => setShowProfilePhotoMenu((value) => !value)} /> : (
                <button
                  type="button"
                  className={`driver-profile-avatar driver-profile-avatar-button ${profilePhotoUrl ? 'driver-profile-avatar-has-photo' : 'driver-profile-avatar-empty'}`}
                  onClick={() => setShowProfilePhotoMenu((value) => !value)}
                  aria-label={text.profilePhotoMenuTitle}
                >
                  {profilePhotoUrl ? <img src={profilePhotoUrl} alt={profileDriverName || text.driver} /> : <span className="driver-profile-avatar-empty-plus">＋</span>}
                  {profilePhotoUrl && <span className="driver-profile-avatar-edit">＋</span>}
                </button>
              )}
              {showProfilePhotoMenu && (
                <div className="driver-profile-avatar-menu" role="menu" aria-label={text.profilePhotoMenuTitle}>
                  <button type="button" role="menuitem" onClick={() => profilePhotoInputRef.current?.click()}>{text.chooseFromAlbum}</button>
                  <button type="button" role="menuitem" onClick={() => profilePhotoCameraInputRef.current?.click()}>{text.takeProfilePhoto}</button>
                </div>
              )}
              <input ref={profilePhotoInputRef} type="file" accept="image/*" className="driver-profile-avatar-input" onChange={(e) => handleProfilePhotoChange(e.target.files?.[0])} />
              <input ref={profilePhotoCameraInputRef} type="file" accept="image/*" capture="environment" className="driver-profile-avatar-input" onChange={(e) => handleProfilePhotoChange(e.target.files?.[0])} />
              <div className="driver-profile-identity-copy">
                <div className="driver-profile-name">{profileDriverName}</div>
                {isFleetMasterAccount && <span className="fleet-profile-account-badge">{text.fleetMasterAccount}</span>}
                {!isFleetMasterAccount && driverProfile?.fleet?.fleet_name && <span className="fleet-profile-account-badge">{text.fleetMembershipLabel} · {driverProfile.fleet.fleet_name}</span>}
                {profileDriverPhone && <div className="driver-profile-phone">{profileDriverPhone}</div>}
                <div className="driver-profile-status-row">
                  <span className={`driver-profile-status-chip ${profileOrderStatusTone}`}>{profileOrderStatusMessage}</span>
                  {driverProfile?.is_vip && <span className="driver-profile-status-chip driver-profile-status-chip-gold">VIP</span>}
                </div>
              </div>
              {isFleetMasterAccount && (
                <div className="fleet-profile-command-strip" data-testid="fleet-profile-command-strip">
                  <button type="button" className="fleet-profile-command-item" onClick={() => openFleetManagement('drivers')}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="8" r="3.2" /><path d="M5 20c.5-4 3-6 7-6s6.5 2 7 6" /></svg>
                    <span>{text.fleetDrivers}</span>
                    <strong>{fleetIdentityStats.drivers ?? '—'}</strong>
                  </button>
                  <button type="button" className="fleet-profile-command-item" onClick={() => openFleetManagement('vehicles')}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 14.5h16l-1.8-5.1A2 2 0 0 0 16.3 8H7.7a2 2 0 0 0-1.9 1.4L4 14.5Z" /><path d="M6 14.5V18h2M16 18h2v-3.5M8 18h8M7.5 12h9" /></svg>
                    <span>{text.fleetVehicles}</span>
                    <strong>{fleetIdentityStats.vehicles ?? '—'}</strong>
                  </button>
                  <button type="button" className="fleet-profile-command-item" onClick={openFleetUpcomingOrders}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="5" y="4" width="14" height="16" rx="2" /><path d="M8 9h8M8 13h8M8 17h4" /></svg>
                    <span>{text.fleetDispatchableOrders}</span>
                    <strong>{fleetDispatchableOrderCount}</strong>
                  </button>
                </div>
              )}
            </section>

            <section className="driver-profile-score-card">
              <div className="driver-profile-wallet-heading">
                <div className="driver-profile-wallet-title">
                  <span className="driver-profile-card-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M4 7.5h14.5A2.5 2.5 0 0 1 21 10v7a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17V8.5A1.5 1.5 0 0 1 4.5 7H18V5.8A1.8 1.8 0 0 0 16.2 4H5.8A2.8 2.8 0 0 0 3 6.8V17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M17 13.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                  <strong>{text.myWallet} / {text.settlement}</strong>
                </div>
                <button
                  type="button"
                  className="driver-profile-wallet-detail"
                  onClick={() => setShowWalletDetails(true)}
                >
                  {text.viewDetails} ›
                </button>
              </div>
              <div className="driver-profile-wallet-stats">
                <button
                  type="button"
                  className="driver-profile-wallet-stat driver-profile-wallet-stat-action"
                  onClick={() => setShowWalletDetails(true)}
                >
                  <span>{text.currentMonthSettlementAmount}</span>
                  <strong>{canViewRevenueStats && !monthlyStatsLoading ? formatProfileSettlementYen(profileSettlementTotals.current_month_settlement_amount) : featureText.zeroCurrency}</strong>
                  <small>{text.monthlyUnsettled}</small>
                </button>
                <button
                  type="button"
                  className="driver-profile-wallet-stat driver-profile-wallet-stat-action"
                  onClick={() => setShowWalletDetails(true)}
                >
                  <span>{text.nextMonthSettlementAmount}</span>
                  <strong>{canViewRevenueStats && !monthlyStatsLoading ? formatProfileSettlementYen(profileSettlementTotals.next_month_settlement_amount) : featureText.zeroCurrency}</strong>
                  <small>{text.driverSettlementRule}</small>
                </button>
                <button
                  type="button"
                  className="driver-profile-wallet-stat driver-profile-wallet-stat-action driver-profile-wallet-score"
                  onClick={() => setShowScoreDetails(true)}
                >
                  <span>{text.driverScore}</span>
                  <strong>{driverScore}</strong>
                  <small>{text.driverScoreHint}</small>
                </button>
              </div>
            </section>

            {canViewRevenueStats && (
              <DriverMonthlyRevenuePanel
                data={monthlyStats}
                loading={monthlyStatsLoading}
                month={monthlyStatsMonth}
                onMonthChange={setMonthlyStatsMonth}
                text={text}
                language={driverLanguage}
              />
            )}

            {!profileSection && (
              <section className="driver-profile-entry-grid">
                {driverProfile?.is_fleet_master && (
                  <button
                    type="button"
                    className="driver-profile-entry-card"
                    data-testid="fleet-management-profile-entry"
                    onClick={() => navigate('/fleet/manage?tab=drivers')}
                  >
                    <span className="driver-profile-card-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M4 20V9.5L12 4l8 5.5V20M3 20h18M8 20v-6h8v6M8.5 10h.01M12 10h.01M15.5 10h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <div>
                      <strong>{text.fleetManagementTitle}</strong>
                      <small>{text.fleetManagementHint}</small>
                    </div>
                    <span className="driver-profile-menu-arrow">›</span>
                  </button>
                )}
                <button type="button" className="driver-profile-entry-card driver-profile-onboarding-card" onClick={() => setProfileSection('onboarding')}>
                  <div className="driver-profile-onboarding-head">
                    <span className="driver-profile-card-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M8 4h8l1 2h2a1.5 1.5 0 0 1 1.5 1.5v11A1.5 1.5 0 0 1 19 20H5a1.5 1.5 0 0 1-1.5-1.5v-11A1.5 1.5 0 0 1 5 6h2l1-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                        <path d="M8 12l2.2 2.2L16 9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <div>
                      <strong>{text.onboardingTitle}</strong>
                      <small>{text.businessStartCheck}</small>
                    </div>
                    <span className="driver-profile-complete-pill">{text.goComplete}</span>
                  </div>
                  <div className="driver-profile-progress-track" aria-hidden="true">
                    {profileProgressSteps.map((step, index) => (
                      <span
                        key={step.key}
                        className={`driver-profile-progress-step ${step.status === 'approved' ? 'is-complete' : ''}`}
                      >
                        <i>{step.status === 'approved' ? '✓' : index + 1}</i>
                        <em>{step.label}</em>
                      </span>
                    ))}
                  </div>
                </button>
                <button type="button" className="driver-profile-entry-card" onClick={() => setProfileSection('document_personal_group')}>
                  <span className="driver-profile-card-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M4.5 5.5A2.5 2.5 0 0 1 7 3h10a2.5 2.5 0 0 1 2.5 2.5v13A2.5 2.5 0 0 1 17 21H7a2.5 2.5 0 0 1-2.5-2.5v-13Z" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8 8h8M8 12h5M8 16h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                  <div>
                    <strong>{text.personalDocumentsTitle}</strong>
                    <small>{text.personalDocumentsHint}</small>
                  </div>
                  {renderDocumentGroupStatus(documentPersonalComplete)}
                  <span className="driver-profile-menu-arrow">›</span>
                </button>
                <button type="button" className="driver-profile-entry-card" onClick={() => setProfileSection('document_vehicle_group')}>
                  <span className="driver-profile-card-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M4 14.5h16l-1.8-5.1A2 2 0 0 0 16.3 8H7.7a2 2 0 0 0-1.9 1.4L4 14.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                      <path d="M6 14.5V18h2M16 18h2v-3.5M8 18h8M7.5 12h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                  <div>
                    <strong>{text.vehicleDocumentsTitle}</strong>
                    <small>{text.vehicleDocumentsHint}</small>
                  </div>
                  {renderDocumentGroupStatus(documentVehicleComplete)}
                  <span className="driver-profile-menu-arrow">›</span>
                </button>
                <button type="button" className="driver-profile-entry-card" onClick={() => setProfileSection('document_bank')}>
                  <span className="driver-profile-card-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M4 10h16M6 10v8M10 10v8M14 10v8M18 10v8M5 18h14M12 4l8 4H4l8-4Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <div>
                    <strong>{text.payoutAccountTitle}</strong>
                    <small>{text.payoutAccountHint}</small>
                  </div>
                  {renderDocumentGroupStatus(documentBankComplete)}
                  <span className="driver-profile-menu-arrow">›</span>
                </button>
              </section>
            )}

            {profileSection === 'onboarding' && (
              <>
                <div className="driver-cert-page-head">
                  <button type="button" className="driver-cert-back-button" aria-label={text.back} onClick={() => setProfileSection('')}>
                    <span aria-hidden="true">‹</span>
                  </button>
                  <div className="driver-cert-title" aria-label={featureText.certification.title}>
                    <span aria-hidden="true">◆</span>
                    <strong>{featureText.certification.title}</strong>
                    <span aria-hidden="true">◆</span>
                  </div>
                </div>

                <section className="driver-cert-summary-card">
                  <div className="driver-cert-summary-main">
                    <span className="driver-cert-avatar" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M12 12.2a3.8 3.8 0 1 0 0-7.6 3.8 3.8 0 0 0 0 7.6Z" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M5.5 20c.7-3.35 3.1-5.35 6.5-5.35 1.45 0 2.72.36 3.78 1.05" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M16 17.2 17.4 18.6 20.5 15.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <div className="driver-cert-summary-copy">
                      <h2>{profileDriverName} · {certificationCompleteCount === certificationProgressTotal ? featureText.certification.complete : featureText.certification.incomplete}</h2>
                      <p>{certificationCompleteCount === certificationProgressTotal ? featureText.certification.completeHint : featureText.certification.incompleteHint}</p>
                    </div>
                  </div>
                  <div className="driver-cert-car" aria-hidden="true">
                    <svg viewBox="0 0 220 120" fill="none">
                      <path d="M25 82c8-28 24-45 47-50 31-7 72-7 102 3 10 3 17 11 21 22l7 22" stroke="currentColor" strokeWidth="5" strokeLinecap="round" opacity="0.32" />
                      <path d="M39 83h154c12 0 20 8 22 19H20c2-12 8-19 19-19Z" fill="currentColor" opacity="0.12" />
                      <path d="M53 75h33M142 75h43" stroke="currentColor" strokeWidth="6" strokeLinecap="round" opacity="0.5" />
                      <path d="M87 35h56c17 0 28 8 35 25H55c7-14 18-22 32-25Z" fill="currentColor" opacity="0.08" />
                    </svg>
                  </div>
                  <div className="driver-cert-progress-row">
                    <div className="driver-cert-progress-count">
                      <strong>{certificationCompleteCount} / {certificationProgressTotal}</strong>
                      <span>{featureText.certification.completedCount}</span>
                    </div>
                    <div className="driver-cert-progress-meter" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={certificationProgressPercent}>
                      <span style={{ width: `${certificationProgressPercent}%` }} />
                    </div>
                    <strong className="driver-cert-progress-percent">{certificationProgressPercent}%</strong>
                  </div>
                  <div className={`driver-cert-review-status-card ${certificationOverallStatus.tone}`}>
                    <span>{text.auditStatus}</span>
                    <strong>{certificationOverallStatus.label}</strong>
                  </div>
                </section>

                <section className="driver-cert-step-list" aria-label={featureText.certification.itemsLabel}>
                  {onboardingCertificationStepKeys.map((step) => {
                    const certStep = certificationStepsByKey[step];
                    const statusMeta = getCertificationStatusMeta(certStep);
                    const isLocked = step === 'final_review' && !certificationPrerequisitesApproved && onboardingSteps.final_review?.status !== 'approved' && onboardingSteps.final_review?.status !== 'submitted';
                    return (
                      <button
                        key={step}
                        type="button"
                        className={`driver-cert-step-card ${isLocked ? 'is-locked' : ''}`}
                        onClick={() => setProfileSection(certStep.targetSection || step)}
                        disabled={isLocked}
                      >
                        <span className="driver-cert-red-dot" aria-hidden="true" />
                        <span className="driver-cert-step-icon" aria-hidden="true">{certStep.icon}</span>
                        <span className="driver-cert-step-copy">
                          <strong>{certStep.title}</strong>
                          <small>{certStep.subtitle}</small>
                          {statusMeta.note && <small className="driver-cert-step-rejection">{text.rejectionReason}: {statusMeta.note}</small>}
                        </span>
                        <span className={`driver-cert-status-pill ${statusMeta.tone}`}>
                          {statusMeta.label}
                          {isLocked && <span className="driver-cert-lock" aria-hidden="true">▣</span>}
                        </span>
                        <span className="driver-cert-chevron" aria-hidden="true">›</span>
                      </button>
                    );
                  })}
                </section>
              </>
            )}

            {profileSection === 'personal_info' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('onboarding')}><span aria-hidden="true">‹</span></button>
            <section className="driver-onboarding-panel">
              <h3>{text.personalInfo}</h3>
              {renderOnboardingDetailProgress('personal_info')}
              {renderOnboardingRejectionReason('personal_info')}
              <div className="driver-onboarding-form-grid">
                <label className="driver-onboarding-field">
                  <span>{text.fullName}</span>
                  <input value={personalInfoForm.full_name || ''} onChange={(e) => setPersonalInfoForm((v) => ({ ...v, full_name: e.target.value }))} placeholder={text.fullName} />
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.dateOfBirth}</span>
                  <input type="date" value={personalInfoForm.date_of_birth || ''} onChange={(e) => setPersonalInfoForm((v) => ({ ...v, date_of_birth: e.target.value }))} />
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.address}</span>
                  <input value={personalInfoForm.address || ''} onChange={(e) => setPersonalInfoForm((v) => ({ ...v, address: e.target.value }))} placeholder={text.address} />
                </label>
                <label className="driver-onboarding-check-option">
                  <input
                    type="checkbox"
                    checked={personalInfoForm.is_japanese_national === true}
                    onChange={(e) => setPersonalInfoForm((v) => ({
                      ...v,
                      is_japanese_national: e.target.checked,
                      residence_card_number: e.target.checked ? '' : v.residence_card_number,
                      residence_card_expiry: e.target.checked ? '' : v.residence_card_expiry,
                      residence_card_long_term_valid: e.target.checked ? false : v.residence_card_long_term_valid,
                    }))}
                  />
                  <span>{text.japaneseNational}</span>
                </label>
                {personalInfoForm.is_japanese_national !== true && (
                  <>
                    <label className="driver-onboarding-field">
                      <span>{text.residenceCardNumber}</span>
                      <input value={personalInfoForm.residence_card_number || ''} onChange={(e) => setPersonalInfoForm((v) => ({ ...v, residence_card_number: e.target.value }))} placeholder={text.residenceCardNumber} />
                    </label>
                    <div className="driver-onboarding-validity-row">
                      <label className="driver-onboarding-field driver-onboarding-validity-date">
                        <span>{text.residenceCardExpiry}</span>
                        <input type="date" value={personalInfoForm.residence_card_expiry || ''} onChange={(e) => setPersonalInfoForm((v) => ({ ...v, residence_card_expiry: e.target.value }))} aria-label={text.residenceCardExpiry} disabled={personalInfoForm.residence_card_long_term_valid === true} />
                      </label>
                      <label className="driver-onboarding-check-option">
                        <input
                          type="checkbox"
                          checked={personalInfoForm.residence_card_long_term_valid === true}
                          onChange={(e) => setPersonalInfoForm((v) => ({ ...v, residence_card_long_term_valid: e.target.checked, residence_card_expiry: e.target.checked ? '' : v.residence_card_expiry }))}
                        />
                        <span>{text.longTermValid}</span>
                      </label>
                    </div>
                  </>
                )}
                <label className="driver-onboarding-field">
                  <span>{text.driverLicenseNumber}</span>
                  <input value={personalInfoForm.driver_license_number || ''} onChange={(e) => setPersonalInfoForm((v) => ({ ...v, driver_license_number: e.target.value }))} placeholder={text.driverLicenseNumber} />
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.driverLicenseExpiry}</span>
                  <input type="date" value={personalInfoForm.driver_license_expiry || ''} onChange={(e) => setPersonalInfoForm((v) => ({ ...v, driver_license_expiry: e.target.value }))} aria-label={text.driverLicenseExpiry} />
                </label>
              </div>
              <div className="driver-onboarding-language-block">
                <div className="driver-onboarding-field-label">{text.driverServiceLanguages}</div>
                <div className="driver-onboarding-language-grid">
                  {DRIVER_SERVICE_LANGUAGE_OPTIONS.map((language) => (
                    <label key={language} className="driver-onboarding-language-option">
                      <input
                        type="checkbox"
                        checked={(personalInfoForm.service_languages || []).includes(language)}
                        onChange={() => togglePersonalServiceLanguage(language)}
                      />
                      <span>{text.driverServiceLanguageNames?.[language] || text.unknownStatus}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="driver-onboarding-upload-grid">
                {[
                  ...(personalInfoForm.is_japanese_national === true ? [] : [
                    ['residence_card_front', text.residenceCardFront],
                    ['residence_card_back', text.residenceCardBack],
                  ]),
                  ['driver_license_front', text.driverLicenseFront],
                  ['driver_license_back', text.driverLicenseBack],
                ].map(([key, label]) => (
                  renderOnboardingUploadControl(
                    key,
                    label,
                    handlePersonalFile,
                    personalFiles[key]?.name || onboarding?.personal_info?.[key]?.name
                  )
                ))}
              </div>
              <button type="button" className="driver-profile-primary-button" onClick={submitPersonalInfo} disabled={onboardingSaving === 'personal_info'}>
                {onboardingSaving === 'personal_info' ? text.submitting : text.submit}
              </button>
            </section>


              </>
            )}

            {profileSection === 'vehicle_info' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('onboarding')}><span aria-hidden="true">‹</span></button>
            <section className="driver-onboarding-panel">
              <h3>{text.vehicleInfo}</h3>
              {renderOnboardingDetailProgress('vehicle_info')}
              {renderOnboardingRejectionReason('vehicle_info')}
              <div className="driver-onboarding-form-grid">
                <div className="driver-onboarding-field md:col-span-3">
                  <span>{text.plateNumber}</span>
                  <div className="driver-onboarding-plate-grid driver-license-plate-card" aria-label={text.plateNumber}>
                    <div className="driver-license-plate-top-row">
                      <input value={vehicleInfoForm.plate_region || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, plate_region: e.target.value }))} placeholder={text.plateRegion} aria-label={text.plateRegion} />
                      <input value={vehicleInfoForm.plate_class || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, plate_class: e.target.value }))} placeholder={text.plateClass} aria-label={text.plateClass} inputMode="numeric" />
                    </div>
                    <div className="driver-license-plate-bottom-row">
                      <input value={vehicleInfoForm.plate_kana || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, plate_kana: e.target.value }))} placeholder={text.plateKana} aria-label={text.plateKana} />
                      <div className="driver-license-plate-digits">
                        <input value={vehicleInfoForm.plate_digits || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, plate_digits: e.target.value }))} placeholder="20-46" aria-label={text.plateDigits} inputMode="numeric" />
                      </div>
                    </div>
                  </div>
                </div>
                <label className="driver-onboarding-field">
                  <span>{text.vehicleModel}</span>
                  <select value={vehicleInfoForm.vehicle_model || 'alphard'} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, vehicle_model: e.target.value }))}>
                    {text.vehicleModelOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.vehicleColor}</span>
                  <select value={vehicleInfoForm.vehicle_color || 'black'} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, vehicle_color: e.target.value }))}>
                    {text.vehicleColorOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.vehicleYear}</span>
                  <input type="number" min="2016" value={vehicleInfoForm.vehicle_year || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, vehicle_year: e.target.value }))} placeholder={text.vehicleYear} />
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.optionalInsuranceCompany}</span>
                  <input value={vehicleInfoForm.optional_insurance_company || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, optional_insurance_company: e.target.value }))} placeholder={text.optionalInsuranceCompany} />
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.optionalInsurancePolicyNumber}</span>
                  <input value={vehicleInfoForm.optional_insurance_policy_number || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, optional_insurance_policy_number: e.target.value }))} placeholder={text.optionalInsurancePolicyNumber} />
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.optionalInsuranceExpiry}</span>
                  <input type="date" value={vehicleInfoForm.optional_insurance_expiry || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, optional_insurance_expiry: e.target.value }))} aria-label={text.optionalInsuranceExpiry} />
                </label>
                <label className="driver-onboarding-field">
                  <span>{text.compulsoryInsuranceExpiry}</span>
                  <input type="date" value={vehicleInfoForm.compulsory_insurance_expiry || ''} onChange={(e) => setVehicleInfoForm((v) => ({ ...v, compulsory_insurance_expiry: e.target.value }))} aria-label={text.compulsoryInsuranceExpiry} />
                </label>
              </div>
              <div className="driver-onboarding-upload-grid">
                {renderOnboardingUploadControl('inspection_certificate', text.inspectionCertificate, handleVehicleFile, vehicleFiles.inspection_certificate?.name || onboarding?.vehicle_info?.inspection_certificate?.name)}
                {renderOnboardingUploadControl('record_certificate', text.recordCertificate, handleVehicleFile, vehicleFiles.record_certificate?.name || onboarding?.vehicle_info?.record_certificate?.name)}
                {renderOnboardingUploadControl('optional_insurance_front', text.optionalInsurancePhoto, handleVehicleFile, vehicleFiles.optional_insurance_front?.name || onboarding?.vehicle_info?.optional_insurance_front?.name)}
                {renderOnboardingUploadControl('compulsory_insurance_photo', text.compulsoryInsurancePhoto, handleVehicleFile, vehicleFiles.compulsory_insurance_photo?.name || onboarding?.vehicle_info?.compulsory_insurance_photo?.name)}
              </div>
              <button type="button" className="driver-profile-primary-button" onClick={submitVehicleInfo} disabled={onboardingSaving === 'vehicle_info'}>
                {onboardingSaving === 'vehicle_info' ? text.submitting : text.submit}
              </button>
            </section>


              </>
            )}

            {profileSection === 'training' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('onboarding')}><span aria-hidden="true">‹</span></button>
            <section className="driver-onboarding-panel">
              <h3>{text.training}</h3>
              {renderOnboardingDetailProgress('training')}
              {renderOnboardingRejectionReason('training')}
              <div className="driver-training-progress-card">
                <div className="driver-training-progress-header">
                  <span>{`${text.trainingLessonProgress} ${Math.min(trainingLessonIndex + 1, trainingLessons.length || 1)} / ${trainingLessons.length || 1}`}</span>
                  <strong>{trainingProgressPercent}%</strong>
                </div>
                <div
                  className="driver-training-progress-bar"
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={trainingProgressPercent}
                >
                  <span style={{ width: `${trainingProgressPercent}%` }} />
                </div>
              </div>
              <div className={currentTrainingLesson.is_critical ? 'driver-training-lesson-card is-critical' : 'driver-training-lesson-card'}>
                <div className="driver-profile-section-label">
                  {text.trainingMaterial}
                  {currentTrainingLesson.is_critical && <span className="driver-training-critical-label">{text.trainingCritical}</span>}
                </div>
                <h4>{currentTrainingLesson.title}</h4>
                <p>{currentTrainingLesson.summary}</p>
                <ul>
                  {(currentTrainingLesson.points || []).map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <div className="driver-training-lesson-actions">
                  <button
                    type="button"
                    className="driver-profile-primary-button driver-profile-secondary-button"
                    onClick={() => setTrainingLessonIndex((current) => Math.max(current - 1, 0))}
                    disabled={trainingLessonIndex === 0}
                  >
                    {text.trainingPrev}
                  </button>
                  {trainingConfirmedLessons[currentTrainingLesson.id] ? (
                    <button
                      type="button"
                      className="driver-profile-primary-button"
                      onClick={() => setTrainingLessonIndex((current) => Math.min(current + 1, trainingLessons.length - 1))}
                      disabled={trainingLessonIndex >= trainingLessons.length - 1}
                    >
                      {trainingLessonIndex >= trainingLessons.length - 1 ? text.trainingConfirmed : text.trainingNext}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="driver-profile-primary-button"
                      onClick={handleConfirmTrainingLesson}
                      disabled={onboardingSaving === 'training_lesson'}
                    >
                      {onboardingSaving === 'training_lesson' ? text.submitting : text.trainingUnderstandPage}
                    </button>
                  )}
                </div>
              </div>
              {trainingWrongQuestions.length > 0 && (
                <div className="driver-training-wrong-note">
                  <strong>{text.trainingWrongHint}</strong>
                  <div className="mt-2 grid gap-2">
                    {trainingWrongQuestions.map((item) => (
                      <button
                        key={item.question_id}
                        type="button"
                        className="driver-training-review-link"
                        onClick={() => {
                          const reviewIndex = trainingLessons.findIndex((lesson) => lesson.id === item.lesson_id);
                          if (reviewIndex >= 0) setTrainingLessonIndex(reviewIndex);
                        }}
                      >
                        {item.lesson_title || item.question}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {trainingLessonsCompleted ? (
                <div className="space-y-3">
                  <div className="driver-training-unlocked-note">{text.trainingStartTest}</div>
                  {trainingQuestions.map((question, index) => (
                    <div key={question.id} className="driver-training-question">
                      <div className="font-bold text-foreground">{index + 1}. {question.question}</div>
                      <div className="mt-2 grid gap-2">
                        {(question.options || []).map((option) => (
                          <label key={option} className="driver-training-option">
                            <input
                              type="radio"
                              name={`training-${question.id}`}
                              checked={trainingAnswers[question.id] === option}
                              onChange={() => setTrainingAnswers((current) => ({ ...current, [question.id]: option }))}
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="driver-training-locked-note">
                  {text.trainingLocked}
                </div>
              )}
              <button type="button" className="driver-profile-primary-button" onClick={submitTraining} disabled={onboardingSaving === 'training' || !trainingLessonsCompleted}>
                {onboardingSaving === 'training' ? text.submitting : text.submitTraining}
              </button>
            </section>


              </>
            )}

            {profileSection === 'vehicle_inspection' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('onboarding')}><span aria-hidden="true">‹</span></button>
            <section className="driver-onboarding-panel">
              <h3>{text.vehicleInspection}</h3>
              {renderOnboardingDetailProgress('vehicle_inspection')}
              {renderOnboardingRejectionReason('vehicle_inspection')}
              <div className="driver-inspection-step-tabs">
                {[
                  ['site', text.inspectionSite],
                  ['appointment', text.appointmentDate],
                  ['confirmation', text.inspectionNoticeTitle],
                  ['payment', text.inspectionPaymentStep],
                  ['qr', text.inspectionQrTitle],
                ].map(([step, label], index) => {
                  const locked = step === 'payment' ? !inspectionAppointmentReady : step === 'qr' ? !inspectionPaymentDone : false;
                  return (
                    <button
                      key={step}
                      type="button"
                      className={`driver-inspection-step-tab ${inspectionStep === step ? 'is-active' : ''} ${locked ? 'is-locked' : ''}`}
                      onClick={() => !locked && setInspectionStep(step)}
                      disabled={locked}
                    >
                      <span>{index + 1}</span>
                      <strong>{label}</strong>
                      <small>{text.onboardingStatus?.[inspectionStepStatus[step]] || inspectionStepStatus[step]}</small>
                    </button>
                  );
                })}
              </div>

              {inspectionStep === 'site' && (
                <div className="driver-inspection-step-body">
                  <div className="driver-inspection-site-card">
                    <div className="driver-profile-section-label">{text.inspectionSite}</div>
                    <strong>{selectedInspectionSite?.name || text.inspectionLocation}</strong>
                    <span>{selectedInspectionSite?.address || text.inspectionAddressPending}</span>
                    <small>{text.inspectionFutureSelectable}</small>
                  </div>
                  <button type="button" className="driver-profile-primary-button" onClick={() => setInspectionStep('appointment')}>
                    {text.appointmentDate}
                  </button>
                </div>
              )}

              {inspectionStep === 'appointment' && (
                <div className="driver-inspection-step-body">
                  <div className="driver-onboarding-form-grid">
                    {(inspectionSites || []).length > 1 ? (
                      <select
                        className="driver-onboarding-select"
                        value={inspectionForm.inspection_site_id || ''}
                        onChange={(e) => setInspectionForm((v) => ({ ...v, inspection_site_id: e.target.value }))}
                        aria-label={text.inspectionSite}
                      >
                        <option value="">{text.inspectionSite}</option>
                        {inspectionSites.map((site) => (
                          <option key={site.id} value={site.id}>{site.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="driver-inspection-site-card">
                        <strong>{selectedInspectionSite?.name || text.inspectionLocation}</strong>
                        <span>{selectedInspectionSite?.address || text.inspectionAddressPending}</span>
                      </div>
                    )}
                    <input type="date" value={inspectionForm.appointment_date || ''} onChange={(e) => setInspectionForm((v) => ({ ...v, appointment_date: e.target.value }))} aria-label={text.appointmentDate} />
                    <select className="driver-onboarding-select" value={inspectionForm.appointment_time || ''} onChange={(e) => setInspectionForm((v) => ({ ...v, appointment_time: e.target.value }))} aria-label={text.appointmentTime}>
                      <option value="">{text.appointmentTime}</option>
                      {(inspectionOptions?.time_slots || []).map((slot) => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                    <input value={inspectionForm.note || ''} onChange={(e) => setInspectionForm((v) => ({ ...v, note: e.target.value }))} placeholder={text.notes} />
                  </div>
                  <button type="button" className="driver-profile-primary-button" onClick={() => setInspectionStep('confirmation')} disabled={!inspectionAppointmentFormReady}>
                    {text.inspectionNoticeTitle}
                  </button>
                </div>
              )}

              {inspectionStep === 'confirmation' && (
                <div className="driver-inspection-step-body">
                  <div className="driver-inspection-notice">
                    <strong>{text.inspectionNoticeTitle}</strong>
                    {(inspectionOptions?.confirmation_items || []).map((item) => (
                      <label key={item.id} className="driver-training-option">
                        <input
                          type="checkbox"
                          checked={(inspectionForm.confirmation_items || []).includes(item.id)}
                          onChange={(e) => setInspectionForm((current) => {
                            const currentItems = new Set(current.confirmation_items || []);
                            if (e.target.checked) currentItems.add(item.id);
                            else currentItems.delete(item.id);
                            return { ...current, confirmation_items: Array.from(currentItems) };
                          })}
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="driver-inspection-fee-warning">
                    {text.inspectionFeeWarning}
                  </div>
                  <button type="button" className="driver-profile-primary-button" onClick={submitVehicleInspection} disabled={onboardingSaving === 'vehicle_inspection' || !inspectionConfirmationReady}>
                    {onboardingSaving === 'vehicle_inspection' ? text.submitting : text.confirmInspectionAppointment}
                  </button>
                </div>
              )}

              {inspectionStep === 'payment' && (
                <div className="driver-inspection-step-body">
                  <div className="driver-inspection-site-card">
                    <div className="driver-profile-section-label">{text.inspectionPaymentStatus}</div>
                    <strong>{getInspectionPaymentStatusLabel(vehicleInspection.payment_status)}</strong>
                    <span>{text.squareTrust}</span>
                    {squarePaymentError && (
                      <span>{squarePaymentError}</span>
                    )}
                  </div>
                  {squarePaymentConfig?.enabled ? (
                    <>
                      <div id="driver-square-card-container" className="driver-square-card-container" />
                      <button type="button" className="driver-profile-primary-button" onClick={submitVehicleInspectionCardPayment} disabled={squarePaymentProcessing || !squareCardReady || !inspectionAppointmentReady}>
                        {squarePaymentProcessing ? text.submitting : `${text.payInspectionFee} ${formatYen(squarePaymentConfig?.fee_jpy || inspectionOptions?.fee_jpy || 5000)}`}
                      </button>
                    </>
                  ) : (
                    <div className="driver-inspection-fee-warning">
                      {text.paymentSetupRequired}
                    </div>
                  )}
                  <button type="button" className="driver-profile-primary-button driver-profile-secondary-button driver-square-payment-fallback-button" onClick={startVehicleInspectionPayment} disabled={onboardingSaving === 'vehicle_inspection_payment' || !inspectionAppointmentReady}>
                    {onboardingSaving === 'vehicle_inspection_payment' ? text.submitting : `${text.payInspectionFee} ${formatYen(inspectionOptions?.fee_jpy || 5000)}`}
                  </button>
                </div>
              )}

              {inspectionStep === 'qr' && inspectionPaymentDone && (
                <div className="driver-inspection-qr-card">
                  <div>
                    <div className="driver-profile-section-label">{text.inspectionQrTitle}</div>
                    <strong>{vehicleInspection.verification_code}</strong>
                    <p>{text.inspectionQrHint}</p>
                    <small>{text.inspectionPaymentStatus}: {getInspectionPaymentStatusLabel(vehicleInspection.payment_status)}</small>
                  </div>
                  {vehicleInspection.qr_image_url && (
                    <img src={vehicleInspection.qr_image_url} alt={text.inspectionQrTitle} />
                  )}
                </div>
              )}
            </section>
              </>
            )}

            {profileSection === 'final_review' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('onboarding')}><span aria-hidden="true">‹</span></button>
                <section className="driver-onboarding-panel">
                  <h3>{text.onboardingSteps?.final_review || text.unknownStatus}</h3>
                  {renderOnboardingDetailProgress('final_review')}
                  {renderOnboardingRejectionReason('final_review')}
                  <div className="driver-inspection-site-card">
                    <div className="driver-profile-section-label">{text.onboardingTitle}</div>
                    <strong>{text.onboardingStatus?.[onboardingSteps.final_review?.status || 'pending'] || text.unknownStatus}</strong>
                    <span>{text.businessStartCheckHint}</span>
                  </div>
                </section>
              </>
            )}

            {profileSection === 'documents' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('')}><span aria-hidden="true">‹</span></button>

                <section className="driver-onboarding-panel">
                  <div className="driver-profile-section-label">{text.documentManagement}</div>
                  {expiredDocumentCount > 0 && (
                    <div className="driver-inspection-fee-warning">{text.documentExpiredAutoPause}</div>
                  )}
                  {upcomingDocumentCount > 0 && expiredDocumentCount === 0 && (
                    <div className="driver-inspection-fee-warning">{text.documentExpiringSoon(upcomingDocumentCount)}</div>
                  )}
                  <div className="driver-profile-section-list">
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_profile')}>
                      <span>{text.documentManagementTitle}</span>
                      <small>{text.fullName} / {text.address}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_license')}>
                      <span>{text.driverLicenseInfo}</span>
                      <small>{text.driverLicenseExpiry}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_vehicle_inspection')}>
                      <span>{text.vehicleInspectionDocument}</span>
                      <small>{text.vehicleInspectionExpiry}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_optional_insurance')}>
                      <span>{text.optionalInsurance}</span>
                      <small>{text.optionalInsuranceExpiry}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_identity')}>
                      <span>{text.residenceOrPassport}</span>
                      <small>{text.residenceCardExpiry} / {text.japaneseNational}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_bank')}>
                      <span>{text.bankAccount}</span>
                      <small>{text.bankName} / {text.bankAccountNumber}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                  </div>
                </section>
              </>
            )}

            {profileSection === 'document_personal_group' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('')}><span aria-hidden="true">‹</span></button>

                <section className="driver-onboarding-panel">
                  <h3>{text.personalDocumentsTitle}</h3>
                  <div className="driver-profile-section-list">
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_profile')}>
                      <span>{text.documentManagementTitle}</span>
                      <small>{text.fullName} / {text.address}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_license')}>
                      <span>{text.driverLicenseInfo}</span>
                      <small>{text.driverLicenseExpiry}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_identity')}>
                      <span>{text.residenceOrPassport}</span>
                      <small>{text.residenceCardExpiry} / {text.japaneseNational}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                  </div>
                </section>
              </>
            )}

            {profileSection === 'document_vehicle_group' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('')}><span aria-hidden="true">‹</span></button>

                <section className="driver-onboarding-panel">
                  <h3>{text.vehicleDocumentsTitle}</h3>
                  <div className="driver-profile-section-list">
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_vehicle_inspection')}>
                      <span>{text.vehicleInspectionDocument}</span>
                      <small>{text.vehicleInspectionExpiry}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                    <button type="button" className="driver-profile-section-list-button" onClick={() => setProfileSection('document_optional_insurance')}>
                      <span>{text.optionalInsurance}</span>
                      <small>{text.optionalInsuranceExpiry}</small>
                      <span className="driver-profile-menu-arrow">›</span>
                    </button>
                  </div>
                </section>
              </>
            )}

            {profileSection === 'document_profile' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('documents')}><span aria-hidden="true">‹</span></button>
                <section className="driver-onboarding-panel">
                  <h3>{text.documentManagementTitle}</h3>
                  {expiredDocumentCount > 0 && (
                    <div className="driver-inspection-fee-warning">{text.documentExpiredAutoPause}</div>
                  )}
                  {upcomingDocumentCount > 0 && expiredDocumentCount === 0 && (
                    <div className="driver-inspection-fee-warning">{text.documentExpiringSoon(upcomingDocumentCount)}</div>
                  )}
                  <div className="driver-onboarding-form-grid">
                    <label className="driver-onboarding-field">
                      <span>{text.fullName}</span>
                      <input value={documentManagementForm.full_name || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, full_name: e.target.value }))} placeholder={text.fullName} />
                    </label>
                    <label className="driver-onboarding-field">
                      <span>{text.address}</span>
                      <input value={documentManagementForm.address || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, address: e.target.value }))} placeholder={text.address} />
                    </label>
                  </div>
                </section>


                <section className="driver-onboarding-panel">
                  <button type="button" className="driver-profile-primary-button" onClick={submitDocumentManagement} disabled={onboardingSaving === 'document_management'}>
                    {onboardingSaving === 'document_management' ? text.submitting : text.saveDocuments}
                  </button>
                </section>
              </>
            )}

            {profileSection === 'document_license' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('documents')}><span aria-hidden="true">‹</span></button>
                <section className="driver-onboarding-panel">
                  <h3>{text.driverLicenseInfo}</h3>
                  <div className="driver-onboarding-form-grid">
                    <label className="driver-onboarding-field">
                      <span>{text.driverLicenseNumber}</span>
                      <input value={documentManagementForm.driver_license_number || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, driver_license_number: e.target.value }))} placeholder={text.driverLicenseNumber} />
                    </label>
                    <label className="driver-onboarding-field">
                      <span>{text.driverLicenseExpiry}</span>
                      <input type="date" value={documentManagementForm.driver_license_expiry || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, driver_license_expiry: e.target.value }))} aria-label={text.driverLicenseExpiry} />
                    </label>
                  </div>
                  <div className="driver-onboarding-upload-grid">
                    {renderOnboardingUploadControl('driver_license_front', text.driverLicenseFront, handleDocumentManagementFile, getDocumentUploadName('driver_license_front'))}
                    {renderOnboardingUploadControl('driver_license_back', text.driverLicenseBack, handleDocumentManagementFile, getDocumentUploadName('driver_license_back'))}
                  </div>
                </section>


                <section className="driver-onboarding-panel">
                  <button type="button" className="driver-profile-primary-button" onClick={submitDocumentManagement} disabled={onboardingSaving === 'document_management'}>
                    {onboardingSaving === 'document_management' ? text.submitting : text.saveDocuments}
                  </button>
                </section>
              </>
            )}

            {profileSection === 'document_vehicle_inspection' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('documents')}><span aria-hidden="true">‹</span></button>
                <section className="driver-onboarding-panel">
                  <h3>{text.vehicleInspectionDocument}</h3>
                  <div className="driver-onboarding-form-grid">
                    <label className="driver-onboarding-field">
                      <span>{text.vehicleInspectionExpiry}</span>
                      <input type="date" value={documentManagementForm.vehicle_inspection_expiry || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, vehicle_inspection_expiry: e.target.value }))} aria-label={text.vehicleInspectionExpiry} />
                    </label>
                  </div>
                  <div className="driver-onboarding-upload-grid">
                    {renderOnboardingUploadControl('inspection_certificate', text.inspectionCertificate, handleDocumentManagementFile, getDocumentUploadName('inspection_certificate'))}
                    {renderOnboardingUploadControl('record_certificate', text.recordCertificate, handleDocumentManagementFile, getDocumentUploadName('record_certificate'))}
                  </div>
                </section>


                <section className="driver-onboarding-panel">
                  <button type="button" className="driver-profile-primary-button" onClick={submitDocumentManagement} disabled={onboardingSaving === 'document_management'}>
                    {onboardingSaving === 'document_management' ? text.submitting : text.saveDocuments}
                  </button>
                </section>
              </>
            )}

            {profileSection === 'document_optional_insurance' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('documents')}><span aria-hidden="true">‹</span></button>
                <section className="driver-onboarding-panel">
                  <h3>{text.optionalInsurance}</h3>
                  <div className="driver-onboarding-form-grid">
                    <label className="driver-onboarding-field">
                      <span>{text.optionalInsuranceCompany}</span>
                      <input value={documentManagementForm.optional_insurance_company || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, optional_insurance_company: e.target.value }))} placeholder={text.optionalInsuranceCompany} />
                    </label>
                    <label className="driver-onboarding-field">
                      <span>{text.optionalInsurancePolicyNumber}</span>
                      <input value={documentManagementForm.optional_insurance_policy_number || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, optional_insurance_policy_number: e.target.value }))} placeholder={text.optionalInsurancePolicyNumber} />
                    </label>
                    <label className="driver-onboarding-field">
                      <span>{text.optionalInsuranceExpiry}</span>
                      <input type="date" value={documentManagementForm.optional_insurance_expiry || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, optional_insurance_expiry: e.target.value }))} aria-label={text.optionalInsuranceExpiry} />
                    </label>
                  </div>
                  <div className="driver-onboarding-upload-grid">
                    <label className="driver-onboarding-upload"><span>{text.optionalInsuranceFront}</span><input type="file" accept="image/*,.pdf" onChange={(e) => handleDocumentManagementFile('optional_insurance_front', e.target.files?.[0])} /></label>
                    <label className="driver-onboarding-upload"><span>{text.optionalInsuranceBack}</span><input type="file" accept="image/*,.pdf" onChange={(e) => handleDocumentManagementFile('optional_insurance_back', e.target.files?.[0])} /></label>
                  </div>
                </section>


                <section className="driver-onboarding-panel">
                  <button type="button" className="driver-profile-primary-button" onClick={submitDocumentManagement} disabled={onboardingSaving === 'document_management'}>
                    {onboardingSaving === 'document_management' ? text.submitting : text.saveDocuments}
                  </button>
                </section>
              </>
            )}

            {profileSection === 'document_identity' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('documents')}><span aria-hidden="true">‹</span></button>
                <section className="driver-onboarding-panel">
                  <h3>{text.residenceOrPassport}</h3>
                  <div className="driver-onboarding-form-grid">
                    <label className="driver-onboarding-check-option">
                      <input
                        type="checkbox"
                        checked={documentManagementForm.is_japanese_national === true}
                        onChange={(e) => setDocumentManagementForm((v) => ({
                          ...v,
                          is_japanese_national: e.target.checked,
                          residence_card_number: e.target.checked ? '' : v.residence_card_number,
                          residence_card_expiry: e.target.checked ? '' : v.residence_card_expiry,
                          residence_card_long_term_valid: e.target.checked ? false : v.residence_card_long_term_valid,
                        }))}
                      />
                      <span>{text.japaneseNational}</span>
                    </label>
                    {documentManagementForm.is_japanese_national !== true && (
                      <>
                        <label className="driver-onboarding-field">
                          <span>{text.residenceCardNumber}</span>
                          <input value={documentManagementForm.residence_card_number || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, residence_card_number: e.target.value }))} placeholder={text.residenceCardNumber} />
                        </label>
                        <div className="driver-onboarding-validity-row">
                          <label className="driver-onboarding-field driver-onboarding-validity-date">
                            <span>{text.validUntil}</span>
                            <input type="date" value={documentManagementForm.residence_card_expiry || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, residence_card_expiry: e.target.value }))} aria-label={text.residenceCardExpiry} disabled={documentManagementForm.residence_card_long_term_valid === true} />
                          </label>
                          <label className="driver-onboarding-check-option">
                            <input
                              type="checkbox"
                              checked={documentManagementForm.residence_card_long_term_valid === true}
                              onChange={(e) => setDocumentManagementForm((v) => ({ ...v, residence_card_long_term_valid: e.target.checked, residence_card_expiry: e.target.checked ? '' : v.residence_card_expiry }))}
                            />
                            <span>{text.longTermValid}</span>
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                  {documentManagementForm.is_japanese_national !== true && (
                    <div className="driver-onboarding-upload-grid">
                      {renderOnboardingUploadControl('residence_card_front', text.residenceCardFront, handleDocumentManagementFile, getDocumentUploadName('residence_card_front'))}
                      {renderOnboardingUploadControl('residence_card_back', text.residenceCardBack, handleDocumentManagementFile, getDocumentUploadName('residence_card_back'))}
                    </div>
                  )}
                </section>


                <section className="driver-onboarding-panel">
                  <button type="button" className="driver-profile-primary-button" onClick={submitDocumentManagement} disabled={onboardingSaving === 'document_management'}>
                    {onboardingSaving === 'document_management' ? text.submitting : text.saveDocuments}
                  </button>
                </section>
              </>
            )}

            {profileSection === 'document_bank' && (
              <>
                <button type="button" className="driver-profile-back-button" aria-label={text.back} onClick={() => setProfileSection('documents')}><span aria-hidden="true">‹</span></button>
                <section className="driver-onboarding-panel">
                  <h3>{text.bankAccount}</h3>
                  <div className="driver-onboarding-form-grid">
                    <label className="driver-onboarding-field">
                      <span>{text.bankName}</span>
                      <input value={documentManagementForm.bank_name || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, bank_name: e.target.value }))} placeholder={text.bankName} />
                    </label>
                    <label className="driver-onboarding-field">
                      <span>{text.bankBranch}</span>
                      <input value={documentManagementForm.bank_branch || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, bank_branch: e.target.value }))} placeholder={text.bankBranch} />
                    </label>
                    <label className="driver-onboarding-field">
                      <span>{text.bankAccountType}</span>
                      <select value={documentManagementForm.bank_account_type || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, bank_account_type: e.target.value }))} aria-label={text.bankAccountType}>
                        {(text.bankAccountTypeOptions || []).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="driver-onboarding-field">
                      <span>{text.bankAccountNumber}</span>
                      <input value={documentManagementForm.bank_account_number || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, bank_account_number: e.target.value }))} placeholder={text.bankAccountNumber} />
                    </label>
                    <label className="driver-onboarding-field">
                      <span>{text.bankAccountHolder}</span>
                      <input value={documentManagementForm.bank_account_holder || ''} onChange={(e) => setDocumentManagementForm((v) => ({ ...v, bank_account_holder: e.target.value }))} placeholder={text.bankAccountHolder} />
                    </label>
                  </div>
                  <button type="button" className="driver-profile-primary-button" onClick={submitDocumentManagement} disabled={onboardingSaving === 'document_management'}>
                    {onboardingSaving === 'document_management' ? text.submitting : text.saveDocuments}
                  </button>
                </section>
              </>
            )}

            <section className="driver-profile-menu">
              <button
                type="button"
                onClick={fetchDriverNotices}
                className="driver-profile-menu-item"
                disabled={driverNoticesLoading}
                data-testid="driver-profile-notices-button"
              >
                <span className="driver-profile-menu-main">
                  <span className="driver-profile-menu-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M5.5 5.8A2.3 2.3 0 0 1 7.8 3.5h8.4a2.3 2.3 0 0 1 2.3 2.3v11.4a2.3 2.3 0 0 1-2.3 2.3H8.3L5.5 21v-3.8A2.3 2.3 0 0 1 3.2 15V5.8Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                      <path d="M8 8.5h8M8 12h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span>{text.driverNoticeHistory}</span>
                </span>
                <span className="driver-profile-menu-arrow">›</span>
              </button>
              <button
                type="button"
                onClick={() => navigateFromOrders('/driver/legal')}
                className="driver-profile-menu-item"
                data-testid="driver-legal-compliance-center-button"
              >
                <span className="driver-profile-menu-main">
                  <span className="driver-profile-menu-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 3.2 19 6v5.4c0 4.4-2.8 7.5-7 9.3-4.2-1.8-7-4.9-7-9.3V6l7-2.8Z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
                      <path d="M8.6 12h6.8M10 9.2h4M9.2 15h5.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span>{text.legalComplianceCenter}</span>
                </span>
                <span className="driver-profile-menu-arrow">›</span>
              </button>
              <button
                type="button"
                onClick={() => navigateFromOrders('/driver/settings')}
                className="driver-profile-menu-item"
                data-testid="driver-settings-button"
              >
                <span className="driver-profile-menu-main">
                  <span className="driver-profile-menu-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 8.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6Z" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M19.4 13.5a7.9 7.9 0 0 0 .05-1.5l2-1.55-2-3.45-2.45 1a7.2 7.2 0 0 0-1.3-.75L15.35 4h-4l-.35 3.25c-.45.2-.88.45-1.28.74L7.25 7l-2 3.45L7.2 12a7.9 7.9 0 0 0 0 1.5l-1.95 1.55 2 3.45 2.47-1c.4.3.83.55 1.28.75l.35 3.25h4l.35-3.25c.45-.2.88-.45 1.3-.75l2.45 1 2-3.45-2.05-1.55Z" stroke="currentColor" strokeWidth="1.55" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span>{text.settings}</span>
                </span>
                <span className="driver-profile-menu-arrow">›</span>
              </button>
            </section>
          </div>
        ) : (filteredOrders.length === 0 && filteredExtraSchedules.length === 0) ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-muted-foreground">{text.noOrders}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedDates.map(date => (
              <div key={date}>
                <h2 className="driver-order-date-header">
                  {formatDate(date)}
                </h2>
                <div className="space-y-3">
                  {groupedItems[date].map(item => (
                    item._type === 'order' ? (
                      // 正式订单卡片
                      (() => {
                        const isNextDriverOrder = nextDriverOrder?.id === item.id;
                        const changeHighlights = getOrderChangeHighlights(item, text);
                        const isFleetManagedOrder = Boolean(item.fleet_owner_order);
                        const assignedFleetDriver = getFleetAssignedDriver(item);
                        const canUseDriverOrderActions = !isFleetManagedOrder || item.assigned_driver_id === driverProfile?.id;
                        const canUseFleetOrderTools = canUseDriverOrderActions || isFleetManagedOrder;
                        const canOpenOrderDetail = canUseDriverOrderActions || isFleetMasterAccount || isFleetManagedOrder;
                        return (
<DriverAssignedOrderCard {...{item,canOpenOrderDetail,navigateFromOrders,isNextDriverOrder,text,statusColors,statusLabel,serviceTypeLabel,OrderSeatBadges,driverLanguage,changeHighlights,formatDriverLocationLine,canViewRevenueStats,formatYen,isFleetManagedOrder,assignedFleetDriver,openFleetDriverAssignment,canUseFleetOrderTools,openReportModal,handleDownloadSignboardPdf,generatingSignboardOrderId,handleAddOrderToCalendar,getDriverCalendarAdded,formatDriverVehicleType}}/>
                        );
                      })()
                    ) : (
                      // 追加订单卡片（司机追加或调度追加）
<DriverExtraOrderCard {...{item,driverLanguage,text,extraStatusColors,extraStatusLabel,OrderSeatBadges,ExtraScheduleReceipt,extraCopy,settlingExtra,changeExtraSettlement,DriverPoolSwipeControl,extraSettlementError,generatingSignboardOrderId,handleDownloadExtraSignboardPdf,DriverTripInstruction,openEditExtra,handleCancelExtra,cancellingExtra}}/>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DriverBottomNavigation activeDriverTab={activeDriverTab} setActiveDriverTab={setActiveDriverTab} text={text} attendanceEnabled={attendanceEnabled} attendanceTitle={attendanceTitle} unreadChatCount={unreadChatCount}/>


      {/* 汇报包车时间弹窗 */}
      {showReportModal && selectedOrder && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="premium-card w-full max-w-sm">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{text.reportEndTime}</h3>
              <button
                onClick={() => { setShowReportModal(false); setSelectedOrder(null); }}
                className="text-muted-foreground hover:text-foreground text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* 订单信息 */}
              <div className="bg-surface rounded-xl p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-muted-foreground">{text.startTime}</span>
                  <span className="font-medium">{selectedOrder.pickup_time || '--:--'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{text.bookedDuration}</span>
                  <span className="font-medium">{selectedOrder.selected_hours || '-'} {text.hours}</span>
                </div>
              </div>

              {/* 时间选择 - Safari 兼容使用原生 DOM */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {text.actualEndTime}
                </label>
                <select
                  ref={selectRef}
                  onChange={(e) => setReportEndTime(e.target.value)}
                  className="premium-input text-base"
                  data-testid="end-time-select"
                />
              </div>

              {/* 提交按钮 */}
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowReportModal(false); setSelectedOrder(null); }}
                  className="flex-1 py-3 border border-border-subtle rounded-xl font-medium hover:bg-surface transition"
                >
                  {text.cancel}
                </button>
                <button
                  onClick={handleReportEndTime}
                  disabled={reporting || !reportEndTime}
                  className="flex-1 py-3 bg-warning text-warning-foreground rounded-xl font-semibold hover:brightness-110 disabled:opacity-50 transition"
                  data-testid="submit-end-time-button"
                >
                  {reporting ? text.submitting : text.submitConfirm}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {fleetDispatchOrder && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-end bg-black/70"
          onClick={closeFleetDriverAssignment}
          data-testid="fleet-driver-assignment-sheet"
        >
          <section
            className="w-full max-h-[82svh] rounded-t-2xl border border-primary/30 bg-[#0b0b0a] shadow-[0_-18px_48px_rgba(0,0,0,0.6)]"
            role="dialog"
            aria-modal="true"
            aria-label={fleetDispatchStep === 'driver' ? text.fleetChooseDriver : text.fleetChooseVehicle}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 pb-4 pt-5">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-primary">
                  {fleetDispatchStep === 'driver' ? text.fleetChooseDriver : text.fleetChooseVehicle}
                </p>
                <h3 className="mt-1 truncate text-lg font-black text-foreground">
                  {fleetDispatchOrder.customer_name || fleetDispatchOrder.passenger_name || fleetDispatchOrder.order_no || text.fleetUnassigned}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">{fleetDispatchOrder.departure_date || '-'} · {fleetDispatchOrder.pickup_time || '--:--'}</p>
              </div>
              <button
                type="button"
                onClick={closeFleetDriverAssignment}
                disabled={Boolean(fleetDispatchSaving)}
                className="shrink-0 rounded-full border border-border-subtle px-3 py-1.5 text-sm font-bold text-muted-foreground disabled:opacity-45"
                aria-label={text.close}
              >
                ×
              </button>
            </div>
            <div className="max-h-[60svh] space-y-2 overflow-y-auto px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              {fleetDispatchLoading ? (
                <p className="py-7 text-center text-sm font-medium text-muted-foreground">{text.loading}</p>
              ) : fleetDispatchStep === 'driver' ? (
                fleetDispatchDrivers.length === 0 ? (
                  <p className="rounded-xl border border-border-subtle bg-surface px-4 py-5 text-center text-sm font-medium text-muted-foreground">{text.fleetNoAvailableDrivers}</p>
                ) : fleetDispatchDrivers.map((fleetDriver) => {
                  const selected = fleetDispatchOrder.assigned_driver_id === fleetDriver.id;
                  return (
                    <button
                      key={fleetDriver.id}
                      type="button"
                      onClick={() => chooseFleetDriver(fleetDriver)}
                      disabled={Boolean(fleetDispatchSaving)}
                      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-4 text-left transition active:scale-[0.99] disabled:opacity-55 ${
                        selected ? 'border-primary/65 bg-primary/12' : 'border-border-subtle bg-surface hover:border-primary/35'
                      }`}
                      data-testid={`fleet-driver-option-${fleetDriver.id}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-base font-bold text-foreground">{fleetDriver.name || text.driver}</span>
                        <span className="mt-1 block text-sm text-muted-foreground">{fleetDriver.phone || '-'}</span>
                      </span>
                      <span className="shrink-0 text-sm font-bold text-primary">{selected ? '✓' : ''}</span>
                    </button>
                  );
                })
              ) : fleetDispatchStep === 'vehicle-create' ? (
                <form className="space-y-4" onSubmit={saveAndAssignFleetVehicle} data-testid="fleet-new-vehicle-form">
                  <div className="rounded-xl border border-primary/25 bg-primary/10 px-4 py-3">
                    <span className="block text-xs font-semibold text-primary">{text.fleetSelectedDriver}</span>
                    <span className="mt-1 block truncate text-sm font-bold text-foreground">{fleetDispatchSelectedDriver?.name || text.driver}{fleetDispatchSelectedDriver?.phone ? ` · ${fleetDispatchSelectedDriver.phone}` : ''}</span>
                  </div>
                  <label className="block">
                    <span className="mb-1 block text-sm font-bold text-foreground">{text.fleetVehicleType}</span>
                    <select
                      className="premium-input h-12 w-full"
                      value={fleetDispatchVehicleDraft.vehicle_type}
                      onChange={(event) => setFleetDispatchVehicleDraft((current) => ({ ...current, vehicle_type: event.target.value }))}
                      disabled={Boolean(fleetDispatchSaving)}
                    >
                      <option value="alphard">アルファード</option>
                      <option value="hiace">ハイエース</option>
                      <option value="premium_hiace">プレミアムハイエース</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-bold text-foreground">{text.fleetPlateNumber}</span>
                    <input
                      required
                      autoFocus
                      className="premium-input h-12 w-full"
                      placeholder={text.fleetPlatePlaceholder}
                      value={fleetDispatchVehicleDraft.plate_number}
                      onChange={(event) => setFleetDispatchVehicleDraft((current) => ({ ...current, plate_number: event.target.value }))}
                      disabled={Boolean(fleetDispatchSaving)}
                    />
                  </label>
                  <p className="text-xs leading-5 text-muted-foreground">{text.fleetVehicleSaveHint}</p>
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <button type="button" className="premium-chip py-3" disabled={Boolean(fleetDispatchSaving)} onClick={() => setFleetDispatchStep('vehicle')}>{text.back}</button>
                    <button type="submit" className="premium-button bg-primary py-3 text-primary-foreground disabled:opacity-55" disabled={Boolean(fleetDispatchSaving) || !fleetDispatchVehicleDraft.plate_number.trim()}>{fleetDispatchSaving === 'new-vehicle' ? text.fleetAssigning : text.fleetSaveAndAssign}</button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-primary/25 bg-primary/10 px-4 py-3">
                    <span className="min-w-0"><span className="block text-xs font-semibold text-primary">{text.fleetSelectedDriver}</span><span className="block truncate text-sm font-bold text-foreground">{fleetDispatchSelectedDriver?.name || text.driver}{fleetDispatchSelectedDriver?.phone ? ` · ${fleetDispatchSelectedDriver.phone}` : ''}</span></span>
                    <button type="button" className="shrink-0 text-xs font-bold text-primary" onClick={() => setFleetDispatchStep('driver')}>{text.change}</button>
                  </div>
                  <button type="button" className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl border border-primary/50 bg-primary/12 px-4 py-3 text-sm font-bold text-primary" onClick={() => setFleetDispatchStep('vehicle-create')} disabled={Boolean(fleetDispatchSaving)} data-testid="fleet-add-vehicle-option">+ {text.fleetAddVehicle}</button>
                  {fleetDispatchVehicles.length === 0 ? (
                    <p className="rounded-xl border border-border-subtle bg-surface px-4 py-5 text-center text-sm font-medium text-muted-foreground">{text.fleetNoSavedVehicles}</p>
                  ) : fleetDispatchVehicles.map((vehicle) => {
                    const saving = fleetDispatchSaving === vehicle.id;
                    const deleting = fleetDispatchSaving === `delete-${vehicle.id}`;
                    const vehicleLabel = [vehicle.vehicle_brand, vehicle.vehicle_model || vehicle.vehicle_type, vehicle.plate_number].filter(Boolean).join(' · ');
                    return (
                      <div
                        key={vehicle.id}
                        className="flex w-full items-center gap-2 rounded-xl border border-border-subtle bg-surface p-2 transition hover:border-primary/35"
                      >
                        <button
                          type="button"
                          onClick={() => assignFleetOrderDriver(vehicle)}
                          disabled={Boolean(fleetDispatchSaving)}
                          className="flex min-w-0 flex-1 items-center justify-between gap-3 px-2 py-2 text-left disabled:opacity-55"
                          data-testid={`fleet-vehicle-option-${vehicle.id}`}
                        >
                          <span className="min-w-0"><span className="block truncate text-base font-bold text-foreground">{vehicleLabel || text.vehicle}</span><span className="mt-1 block text-sm text-muted-foreground">{formatDriverVehicleType(vehicle.vehicle_type)}</span></span>
                          <span className="shrink-0 text-sm font-bold text-primary">{saving ? text.fleetAssigning : text.fleetAssignVehicle}</span>
                        </button>
                        <button type="button" onClick={(event) => deleteFleetVehicle(event, vehicle)} disabled={Boolean(fleetDispatchSaving)} className="shrink-0 rounded-lg border border-destructive/35 px-2 py-2 text-xs font-bold text-destructive disabled:opacity-45" aria-label={`${text.fleetDeleteVehicle} ${vehicle.plate_number || ''}`}>{deleting ? '…' : text.fleetDeleteVehicle}</button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </section>
        </div>,
        document.body
      )}

      {/* 追加订单弹窗 */}
      <ExtraScheduleModal
        isOpen={showExtraModal}
        onClose={() => { setShowExtraModal(false); setEditingExtra(null); }}
        onSuccess={() => { fetchExtraSchedules(); }}
        editingSchedule={editingExtra}
      />

      <DriverTimeOffModal
        isOpen={showTimeOffModal}
        onClose={() => setShowTimeOffModal(false)}
      />

      {showWalletDetails && createPortal(
        <div className="driver-wallet-detail-overlay" data-testid="driver-wallet-detail-modal">
          <section className="driver-wallet-detail-sheet" role="dialog" aria-modal="true" aria-label={text.walletDetailsTitle}>
            <div className="driver-wallet-detail-header">
              <div>
                <p>{text.myWallet}</p>
                <h3>{text.walletDetailsTitle}</h3>
              </div>
              <button type="button" onClick={() => setShowWalletDetails(false)} aria-label={text.close}>×</button>
            </div>
            <div className="driver-wallet-detail-summary">
              <div>
                <span>{text.currentMonthSettlementAmount}</span>
                <strong>{formatProfileSettlementYen(profileSettlementTotals.current_month_settlement_amount)}</strong>
              </div>
              <div>
                <span>{text.nextMonthSettlementAmount}</span>
                <strong>{formatProfileSettlementYen(profileSettlementTotals.next_month_settlement_amount)}</strong>
              </div>
            </div>
            <div className="driver-wallet-detail-list">
              <h4 className="driver-wallet-detail-section-title">{text.walletDetailsTitle}</h4>
              {monthlyStatsLoading ? (
                <div className="driver-wallet-detail-empty">{text.loading}</div>
              ) : walletDetailItems.length === 0 ? (
                <div className="driver-wallet-detail-empty">{text.walletNoDetails}</div>
              ) : (
                walletDetailItems.map((item) => (
                  <article className="driver-wallet-detail-item" key={item.id || item.orderNo}>
                    <div className="driver-wallet-detail-item-head">
                      <div>
                        <strong>{item.orderNo}</strong>
                        <span>{item.date || '-'}</span>
                      </div>
                      <span className={`driver-wallet-payment-chip ${item.paymentTone}`}>{item.paymentLabel}</span>
                    </div>
                    <p className="driver-wallet-detail-route">{item.route}</p>
                    <div className="driver-wallet-detail-grid">
                      <div>
                        <span>{text.walletOrderAmount}</span>
                        <strong>{item.orderAmount === null ? '-' : formatProfileSettlementYen(item.orderAmount)}</strong>
                      </div>
                      <div>
                        <span>{text.walletOvertimeFee}</span>
                        <strong>{formatProfileSettlementYen(item.overtimeFee)}</strong>
                      </div>
                      <div>
                        <span>{text.walletSettlementDate}</span>
                        <strong>{item.settlementDate}</strong>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>,
        document.body
      )}

      {showScoreDetails && createPortal(
        <div className="driver-wallet-detail-overlay" data-testid="driver-score-detail-modal">
          <section className="driver-wallet-detail-sheet" role="dialog" aria-modal="true" aria-label={text.scoreDetailsTitle}>
            <div className="driver-wallet-detail-header">
              <div>
                <p>{text.myWallet}</p>
                <h3>{text.scoreDetailsTitle}</h3>
              </div>
              <button type="button" onClick={() => setShowScoreDetails(false)} aria-label={text.close}>×</button>
            </div>
            <div className="driver-wallet-detail-summary driver-score-detail-summary">
              <div>
                <span>{text.driverScore}</span>
                <strong>{driverScore}</strong>
              </div>
            </div>
            <div className="driver-wallet-detail-list driver-score-detail-list">
              <h4 className="driver-wallet-detail-section-title">{text.scoreDetailsTitle}</h4>
              {scoreEventsLoading ? (
                <div className="driver-wallet-detail-empty">{text.loading}</div>
              ) : scoreEventItems.length === 0 ? (
                <div className="driver-wallet-detail-empty">{text.scoreNoDetails}</div>
              ) : (
                scoreEventItems.map((item) => (
                  <article className="driver-score-detail-item" key={item.id}>
                    <div className="driver-wallet-detail-item-head">
                      <div>
                        <strong>{item.label}</strong>
                        <span>{item.date}</span>
                      </div>
                      <span className={`driver-score-delta ${item.tone}`}>{item.pointsLabel}</span>
                    </div>
                    <div className="driver-score-detail-meta">
                      <span>{text.orderNo}: {item.orderNo}</span>
                      <span>{text.scoreOldNew}: {item.oldScore ?? '-'} → {item.newScore ?? '-'}</span>
                    </div>
                    {item.note && <p className="driver-wallet-detail-route">{item.note}</p>}
                  </article>
                ))
              )}
            </div>
          </section>
        </div>,
        document.body
      )}

      {showNoticesModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="driver-profile-notices-modal">
          <div className="premium-card w-full max-w-md max-h-[86vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{text.driverNoticeTitle}</h3>
              <button
                type="button"
                onClick={() => setShowNoticesModal(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={text.close}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              {driverNotices.length === 0 ? (
                <div className="text-sm text-muted-foreground">{text.driverNoticeEmpty}</div>
              ) : (
                driverNotices.map((notice) => {
                  const localizedNotice = getLocalizedDriverNotice(notice, driverLanguage);
                  return (
                  <article key={notice.id} className="rounded-lg border border-border-subtle bg-background p-4">
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="font-semibold text-foreground">{localizedNotice.title}</h4>
                      {!notice.read_at && <span className="premium-chip text-xs">{text.unreadMessage}</span>}
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">{localizedNotice.body}</p>
                    {notice.published_at && (
                      <p className="mt-3 text-xs text-muted-foreground">{new Date(notice.published_at).toLocaleString()}</p>
                    )}
                  </article>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}

function DriverMonthlyRevenuePanel({ data, loading, month, onMonthChange, text, language }) {
  const totals = data?.totals || {};
  const orders = data?.orders || [];
  const visibleOrders = orders;

  return (
    <div className="driver-monthly-revenue-panel" data-testid="driver-monthly-revenue-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="driver-profile-section-label">{text.monthlyRevenueTitle}</p>
          <p className="mt-1 text-xs text-muted-foreground">{formatDriverMonthLabel(month, language)}</p>
        </div>
        <MonthSelect
          value={month}
          onChange={onMonthChange}
          className="premium-input w-36 text-xs"
          dataTestId="driver-monthly-revenue-month-select"
          monthsForward={1}
          formatLabel={(value) => formatDriverMonthLabel(value, language)}
        />
      </div>
      <div className="driver-monthly-revenue-grid">
        <div className="driver-monthly-revenue-stat">
          <div className="driver-monthly-revenue-value">{loading ? '-' : (totals.completed_order_count || 0)}</div>
          <div className="driver-monthly-revenue-label">{text.monthlyCompletedOrders}</div>
        </div>
        <div className="driver-monthly-revenue-stat">
          <div className="driver-monthly-revenue-value">{loading ? '-' : formatYen(totals.total_reward_amount)}</div>
          <div className="driver-monthly-revenue-label">{text.monthlyReward}</div>
        </div>
        <div className="driver-monthly-revenue-stat">
          <div className="driver-monthly-revenue-value">{loading ? '-' : formatYen(totals.current_month_settlement_amount)}</div>
          <div className="driver-monthly-revenue-label">{text.currentMonthSettlementAmount}</div>
        </div>
        <div className="driver-monthly-revenue-stat">
          <div className="driver-monthly-revenue-value">{loading ? '-' : formatYen(totals.next_month_settlement_amount)}</div>
          <div className="driver-monthly-revenue-label">{text.nextMonthSettlementAmount}</div>
        </div>
      </div>
      <div className="driver-monthly-revenue-rule">{text.driverSettlementRule}</div>
      {visibleOrders.length > 0 && (
        <div className="mt-3 divide-y divide-border-subtle text-xs">
          {visibleOrders.map(order => (
            <div key={order.order_id || order.order_no} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">
                  {[order.departure_date, order.pickup_time].filter(Boolean).join(' ') || order.order_no}
                </p>
                <p className="truncate text-muted-foreground">{order.route_summary || order.customer_name || '-'}</p>
              </div>
              {order.driver_dispatch_price_visible ? (
                <span className="shrink-0 font-semibold text-emerald-700">
                  {formatYen(order.driver_dispatch_price_amount)}
                </span>
              ) : (
                    <span className="shrink-0 text-muted-foreground">-</span>
              )}
            </div>
          ))}
        </div>
      )}
      {(totals.unpriced_order_count || 0) > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
              {text.dispatchPrice}: -
        </p>
      )}
    </div>
  );
}
